import React, {useState, useEffect, useMemo, useRef} from 'react'
import _ from 'lodash'

import {
    Button,
    Card,
    Colors,
    H3,
    IconButton,
    Panel,
    ProgressBar,
    Row,
    Spinner
} from '../../components/UI/index.js'
import {
    formSpacing,
    Input,
    Select
} from '../../components/UI/forms/index.js'
import SearchBar from '../../components/SearchBar/index.js'
import useAnalyticsStore, {
    setAnalyticsDates,
    setLastFetchedStartDate,
    setLastFetchedEndDate,
    setAnalyticsQueries,
    getAnalyticsModels,
    setSelectedAnalytics,
    setSelectedGroup,
    setSubGroup,
    setShowFilterCard,
    setSelectedSpecific,
    filterQueries
} from '../../stores/AnalyticsStore.js'
import InsightsBar from '../../components/analytics/InsightsBar.js'
// import AnalyticsTopBar from '../../components/analytics/TopBar.js'
// import DonutChart from '../../components/charts/DonutChart.js'
import HorizontalBarChart from '../../components/charts/HorizontalBarChart.js'
import HorizontalBarChartStacked, {
    HorizontalBarChartStackedProps
} from '../../components/charts/HorizontalBarChartStacked.js'
import MultiLineChart, {
    MultiLineChartProps
} from '../../components/charts/MultiLineChart.js'
import VerticalBarChart, {
    VerticalBarChartProps
} from '../../components/charts/VerticalBarChart.js'
import VerticalBarChartStacked, {
    VerticalBarChartStackedProps
} from '../../components/charts/VerticalBarChartStacked.js'
import moment, {Moment} from 'moment'
import {Reseller} from '../../../server/functions/resellers/resellers.types.js'
import guid from '../../../utils/guid.js'
import socket from '../../socketio/socketio.js'
import {
    AddressesModel,
    StopsModel
} from '../../../server/functions/analytics/analytics.types.js'
import {useResizeObserver} from '../../helpers/useResizeObserver.js'
import AnalyticsView, {GroupOptions, LeftViewProps, Legend, MainViewProps} from '@components/analytics/AnalyticsView.js'

export interface AnalyticsViewType {
    title: string
    icon: string
    key:
        | 'amountOfOrders'
        | 'amountOfAddresses'
        | 'amountOfColli'
        | 'amountOfParcels'
        | 'plannedStops'
        | 'amountOfDeliveryAttempts'
        | 'differenceRouteDuration'
        | 'colloStatus'
    subFilterBar?: {
        groupOptions?: GroupOptions
        specificOptions?: {[key: string]: {value: string, title: string}[]}
    }
    leftView?: (props: LeftViewProps) => React.JSX.Element
    mainView?: (props: MainViewProps) => React.JSX.Element
    disabled?: boolean
}

interface AnalyticsProps {
    reseller: Reseller
    history: any
    branding: any
}

function getDateGroups(
    startDate: Moment,
    endDate: Moment,
    interval: 'day' | 'week' | 'month'
) {
    const current = startDate.clone().startOf(interval)
    const dateGroups: Moment[] = []

    while (current.isBefore(endDate) || current.isSame(endDate, interval)) {
        dateGroups.push(current.clone())
        current.add(1, interval) // Increment based on the interval
    }

    return dateGroups
}

function addCountsRecursive(obj: any, bundles: Set<string>): number {
    if (typeof obj.count === 'number') {
        Object.keys(obj.bundles || {}).forEach((bundleId) => bundles.add(bundleId))
        return obj.count
    }
    let total = 0
    for (const value of Object.values(obj)) {
        total += addCountsRecursive(value, bundles)
    }
    return total
}

// Stops of a bundle are kept by bundle id instead of in count (see insertNestedData in
// server/functions/analytics/analytics.ts), every bundle in obj counts as one stop
function sumCountsRecursive(obj: any): number {
    const bundles = new Set<string>()
    const total = addCountsRecursive(obj, bundles)
    return total + bundles.size
}

// Sums the objects with the same key together, so a bundle spread over these objects still counts once per key
function sumCountsPerKey(entries: [string, any][]): Map<string, number> {
    const objectsPerKey = new Map<string, any[]>()

    entries.forEach(([key, obj]) => {
        if (!objectsPerKey.has(key)) objectsPerKey.set(key, [])
        objectsPerKey.get(key).push(obj)
    })

    return new Map([...objectsPerKey].map(([key, objects]) => [key, sumCountsRecursive(objects)]))
}

function pluckLevel(obj: any, depth: number): any[] {
    if (depth === 0) return [obj]

    const values = Object.values(obj)
    return values.flatMap((v) => pluckLevel(v, depth - 1))
}

function shouldSplitAnalyticsGroup(group: string): boolean {
    return group === 'option' || group === 'deliveryZone'
}

function splitAnalyticsGroupValues(value: string): string[] {
    return value.split(',').map((v) => v.trim()).filter((v) => v)
}

// type selectedGroup =
//     | 'total'
//     | 'handledBy'
//     | 'customer'
//     | 'messenger'
//     | 'vehicleType'
//     | 'vehicle'
//     | 'route'
//     | 'deliveryZone'
//     | 'postalCodeZone'
//     | 'deliveryZone'
//     | 'option'
//     | 'amountOfDaysBetweenHubScanAndCompleted'
//     | 'amountOfDaysTillDelivery'
//     | 'isCompleted'
//     | 'isDelivered'

export const groupDepths = {
    total: 0,
    customer: 1,
    postalCodeZone: 3,
    deliveryZone: 4,
    option: 5,
    amountOfDaysBetweenHubScanAndCompleted: 6
}

export const groupReversedDepths = {
    total: 0,
    amountOfDaysBetweenHubScanAndCompleted: 1,
    option: 2,
    deliveryZone: 3,
    postalCodeZone: 4,
    type: 5,
    customer: 6
}

export const parcelsGroupDepth = {
    total: 0,
    customer: 1,
    carrier: 2,
    status: 3,
    option: 4,
    koppeling: 5,
    amountOfDaysTillDelivery: 6,
    country: 7
}

export const parcelsGroupReversedDepth = {
    total: 0,
    country: 1,
    amountOfDaysTillDelivery: 2,
    koppeling: 3,
    option: 4,
    status: 5,
    carrier: 6,
    customer: 7
}

export const groupStopsDepth = {
    total: 0,
    customer: 1,
    messenger: 2,
    vehicleType: 3,
    vehicle: 4,
    route: 5,
    deliveryZone: 6,
    postalCodeZone: 7,
    option: 8,
    isCompleted: 9,
    isDelivered: 10
}

export const groupRoutesDepth = {
    total: 0,
    routeId: 1,
    route: 2,
    messenger: 3,
    messengerId: 4,
    vehicleType: 5,
    vehicle: 6,
    totalAddresses: 7,
    completedAddresses: 8,
    completionRate: 9,
    plannedDuration: 10,
    duration: 11
}

export interface ViewData {
    specificId: string
    name: string
    modelDepth: number
    amount: number
    obj?: object
    deliveryAttemptsPercentages?: {
        delivered: number
        offered: number
    }
    deliveryAttempts?: {
        delivered: number
        offered: number
    }
    differenceInRouteDuration?: number
}

const legendColorMap = {
    0: '#4A78A2',
    1: '#719EB0',
    2: '#7DBDC0',
    3: '#748F62',
    4: '#9AC394',
    5: '#BD9759',
    6: '#F2C376',
    7: '#D1D1D1'
}

const analyticsViews: AnalyticsViewType[] = [
    {
        title: 'Aantal stops',
        icon: 'mdi mdi-home-outline',
        key: 'amountOfAddresses',
        subFilterBar: {
            groupOptions: [
                {
                    value: 'total',
                    title: 'Totaal'
                },
                // {
                //     value: 'handledBy',
                //     title: 'Vervoerder'
                // },
                {
                    value: 'customer',
                    title: 'Klant'
                },
                {
                    value: 'postalCodeZone',
                    title: 'Postcodegebied'
                },
                {
                    value: 'deliveryZone',
                    title: 'Bezorggebied'
                },
                {
                    value: 'option',
                    title: 'Verzendoptie'
                },
                {
                    value: 'amountOfDaysBetweenHubScanAndCompleted',
                    title: 'Aantal dagen tot bezorging',
                    info: 'Het aantal dagen vanaf de oorspronkelijke datum van de rit tot en met de afrondingsdatum. Dagen waarop de vestiging gesloten is worden niet meegerekend.'
                }
            ]
        },
        leftView: ({loading, subGroup, leftViewData, groupingIsReversed, legend, selectedGroup, selectedSpecific, setSelectedSpecific}) => {
            const chartContainerRef = useRef<HTMLDivElement>(null)
            const [sortDescend, setSortDescend] = useState(true)
            const [chartContainerWidth, setChartContainerWidth] = useState(440)
            const [chartContainerHeight, setChartContainerHeight] = useState<number | null>(null)
            const [search, setSearch] = useState('')
            const [highlightedXTick, setHighlightedXTick] = useState('')
            const [sortOn, setSortOn] = useState<'amount' | 'alphabetic'>(
                'amount'
            )

            const amountOfAddressesPerGroupData = useMemo(() => {
                if (loading) {
                    return []
                }

                let data = leftViewData
                    .filter((group) => group.amount > 0)
                    .sort((a, b) => {
                        if (sortOn === 'amount') {
                            return b.amount - a.amount
                        }

                        if (sortOn === 'alphabetic') {
                            return a.name.localeCompare(b.name)
                        }
                    })
                    .map((group) => {
                        if (subGroup !== 'total' && subGroup !== '') {
                            const subGroupLevel = groupingIsReversed ? groupReversedDepths[subGroup] : groupDepths[subGroup]

                            const subGroupEntries = Object.entries(group.obj)
                                .flatMap(([, obj]) => {
                                    let subGroupObj = pluckLevel(
                                        obj,
                                        subGroupLevel - group.modelDepth - 1
                                    )

                                    if (shouldSplitAnalyticsGroup(subGroup)) {
                                        let newSubGroupObj = []
                                        subGroupObj.forEach((groupObj) => {
                                            Object.entries(groupObj).forEach(([optionsString, obj]) => {
                                                const options = splitAnalyticsGroupValues(optionsString)
                                                options.forEach((option) => {
                                                    newSubGroupObj.push({
                                                        [option]: obj
                                                    })
                                                })
                                            })
                                        })

                                        subGroupObj = newSubGroupObj
                                    }

                                    return subGroupObj.flatMap((groupObj) => Object.entries(groupObj))
                                })

                            const subGroups = [...sumCountsPerKey(subGroupEntries)]
                                .map(([key, amount]) => ({key, amount}))
                                .toSorted((a, b) => a.amount - b.amount)

                            return {
                                name: group.name || 'Onbekend',
                                value: group.amount,
                                values: subGroups.map((subGroup) => {
                                    return {
                                        value: subGroup.amount,
                                        key: subGroup.key
                                    }
                                })
                            }
                        } else {
                            let color: undefined | string

                            const legendEntry = legend.find((l) => l.label === group.name)
                            if (legendEntry) {
                                color = legendEntry.color
                            }

                            return {
                                name: group.name || 'Onbekend',
                                value: group.amount,
                                color
                            }
                        }
                    })

                if (subGroup !== 'total' && subGroup !== '') {
                    const allKeys = _.uniq(
                        data.flatMap((group) => group.values.map((v) => v.key))
                    )
                    let allKeysWithSum = allKeys
                        .map((key) => {
                            let sum = 0
                            data.forEach((group) => {
                                group.values.forEach((v) => {
                                    if (v.key === key) {
                                        sum += v.value
                                    }
                                })
                            })
                            return {
                                key,
                                amount: sum
                            }
                        })
                        .toSorted((a, b) => b.amount - a.amount)

                    const restAmount = allKeysWithSum.slice(7).reduce((sum, key) => {
                        sum += key.amount
                        return sum
                    }, 0)
                    allKeysWithSum = [
                        ...allKeysWithSum.slice(0, 7),
                        {key: 'Overig', amount: restAmount}
                    ]

                    const coloredKeys = allKeysWithSum.slice(0, 7).map((k) => k.key)
                    data = data.map((group) => {
                        const newValues = []
                        let restSum = 0
                        group.values.forEach((value) => {
                            if (coloredKeys.includes(value.key)) {
                                newValues.push(value)
                            } else {
                                restSum += value.value
                            }
                        })

                        newValues.sort((a, b) => {
                            const legendIndexA = legend.findIndex((l) => l.label === a.key)
                            const legendIndexB = legend.findIndex((l) => l.label === b.key)
                            return legendIndexA - legendIndexB
                        })

                        if (restSum > 0) {
                            newValues.push({key: 'Overig', value: restSum})
                        }
                        group.values = newValues

                        return group
                    })
                }

                if (!sortDescend) {
                    data = data.reverse()
                }

                return data
            }, [leftViewData, sortDescend, sortOn, subGroup, loading])

            const colorMap = useMemo(() => {
                if (amountOfAddressesPerGroupData[0]?.values) {
                    const allKeys = _.uniq(
                        amountOfAddressesPerGroupData.flatMap((group) =>
                            group.values.map((v) => v.key)
                        )
                    )

                    const result = {}

                    allKeys.forEach((key, index) => {
                        const color = legend.find((l) => l.label === key)?.color
                        result[key] = color || legendColorMap[index] || '#D1D1D1'
                    })
                    return result
                } else {
                    return undefined
                }
            }, [amountOfAddressesPerGroupData])

            useEffect(() => {
                if (search) {
                    const result = amountOfAddressesPerGroupData.filter((group) => {
                        const parts = group.name.split(
                            new RegExp(
                                `(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
                                'gi'
                            )
                        )
                        return parts.some(
                            (part) => part.toLowerCase() === search.toLowerCase()
                        )
                    })
                    if (result.length === 1) {
                        setHighlightedXTick(result[0].name)
                    } else {
                        setHighlightedXTick('')
                    }
                } else {
                    setHighlightedXTick('')
                }
            }, [search])

            useResizeObserver({
                ref: chartContainerRef,
                onResize: (width, height) => {
                    setChartContainerWidth(width)
                    if (!chartContainerHeight) {
                        setChartContainerHeight(height)
                    }
                }
            })

            // let title = 'Aantal stops'

            //     const groupPart = {
            //         customer: 'per klant',
            //         messenger: 'per koerier',
            //         vehicleType: 'per voertuigsoort',
            //         vehicle: 'per voertuig',
            //         route: 'per route',
            //         deliveryZone: 'per bezorggebied',
            //         postalCodeZone: 'per postcodegebied',
            //         option: 'per verzendoptie',
            //         amountOfDaysBetweenHubScanAndCompleted:
            // 'per aantal dagen tot bezorging'
            //     }

            if (selectedGroup !== 'total') {
                // title += ` ${groupPart[selectedGroup]}`
            }

            if (subGroup) {
                // title += ` ${groupPart[subGroup]}`
            }

            return (
                <div style={{height: '100%', padding: '12px 0'}}>
                    <Card
                        style={{
                            width: '100%',
                            height: '100%',
                            flex: 1,
                            padding: 12,
                            paddingTop: 0,
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        shadow
                    >
                        <div style={{marginBottom: 12}} />
                        <H3 style={{textAlign: 'center', marginBottom: 12}}>Totaal over periode</H3>
                        <div style={{marginBottom: 12}} />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: formSpacing.columnGap,
                                marginBottom: 24,
                                position: 'sticky',
                                top: 0,
                                paddingTop: 12,
                                backgroundColor: Colors.white
                            }}
                        >
                            <IconButton
                                icon={`mdi mdi-sort-${
                                    sortDescend ? 'descending' : 'ascending'
                                }`}
                                onClick={() => setSortDescend(!sortDescend)}
                            />
                            <Select
                                value={sortOn}
                                onChange={(e) => setSortOn(e.target.value as typeof sortOn)}
                                options={[
                                    {
                                        value: 'amount',
                                        title: 'Aantal'
                                    },
                                    {
                                        value: 'alphabetic',
                                        title: 'Alfabetisch'
                                    }
                                ]}
                                label='Sorteer op'
                            />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder='Zoeken'
                                append='mdi mdi-magnify'
                            />
                        </div>
                        <div style={{overflow: 'auto', flex: 1}}>
                            <div style={{minHeight: '100%', maxHeight: '100%'}} ref={chartContainerRef}>
                                {!loading && (
                                    <>
                                        {(subGroup === 'total' || subGroup === '') ?
                                            (
                                                <HorizontalBarChart
                                                    width={chartContainerWidth}
                                                    height={chartContainerHeight}
                                                    barHeight={24}
                                                    data={amountOfAddressesPerGroupData}
                                                    showNameInShortBar={false}
                                                    activeXTick={selectedSpecific}
                                                    onClickXTick={(tick) =>
                                                        setSelectedSpecific(
                                                            selectedSpecific === tick ? undefined : tick
                                                        )
                                                    }
                                                    highlightedXTick={highlightedXTick}
                                                    disableHover={selectedGroup['amountOfAddresses'] === 'total'}
                                                />
                                            ) :
                                            (
                                                <HorizontalBarChartStacked
                                                    width={chartContainerWidth}
                                                    height={chartContainerHeight}
                                                    barHeight={24}
                                                    data={amountOfAddressesPerGroupData as HorizontalBarChartStackedProps['data']}
                                                    showNameInShortBar={false}
                                                    showValueInBar={false}
                                                    activeXTick={selectedSpecific}
                                                    onClickXTick={(tick) =>
                                                        setSelectedSpecific(
                                                            selectedSpecific === tick ? undefined : tick
                                                        )
                                                    }
                                                    highlightedXTick={highlightedXTick}
                                                    colorMap={colorMap}
                                                    // isPercentage
                                                    disableHover={selectedGroup['amountOfAddresses'] === 'total'}
                                                />
                                            )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            )
        },
        mainView: ({loading, subGroup, selectedGroup, selectedSpecific, setSelectedSpecific, mainViewData, analyticsStartDate, analyticsEndDate, groupingIsReversed, analyticsModel, analyticsModelReversed, legend, history}) => {
            const [chartView, setChartView] = useState<'total' | 'trend'>(
                'total'
            )

            const daysInRange = useMemo(() => {
                const startDate = moment(analyticsStartDate)
                const endDate = moment(analyticsEndDate)
                return endDate.diff(startDate, 'days')
            }, [analyticsStartDate, analyticsEndDate])

            const [chartInterval, setChartInterval] = useState<'day' | 'week' | 'month'>('day')
            const chartContainerRef = useRef<HTMLDivElement>(null)
            const [chartContainerWidth, setChartContainerWidth] = useState(1200)
            const [chartContainerHeight, setChartContainerHeight] = useState(700)
            const [forecast] = useState(false)

            const groupBarCharts = useMemo(() => {
                return !!subGroup || (selectedGroup !== 'total' && !selectedSpecific)
            }, [selectedGroup, subGroup, selectedSpecific])

            useResizeObserver({
                ref: chartContainerRef,
                onResize: (width, height) => {
                    setChartContainerWidth(width)
                    setChartContainerHeight(height)
                }
            })

            useEffect(() => {
                if (daysInRange < 32) {
                    setChartInterval('day')
                } else if (daysInRange >= 32 && daysInRange < 100) {
                    setChartInterval('week')
                } else {
                    setChartInterval('month')
                }
            }, [daysInRange])

            const modelByInterval = useMemo(() => {
                const map: Map<string, AddressesModel[number][]> = new Map()

                Object.entries(groupingIsReversed ? analyticsModelReversed : analyticsModel).forEach(([date, obj]) => {
                    const key = moment(date).startOf(chartInterval).format('YYYY-MM-DD')

                    if (!map.has(key)) map.set(key, [])
                    map.get(key).push(obj)
                })

                return map
            }, [analyticsModel, analyticsModelReversed, chartInterval, groupingIsReversed])

            const sumCache = new WeakMap()

            function sumCountsRecursiveCached(obj: any): number {
                if (!obj) return 0

                const cached = sumCache.get(obj)
                if (cached !== undefined) {
                    return cached
                }

                const value = sumCountsRecursive(obj)
                sumCache.set(obj, value)

                return value
            }

            const chartData = useMemo(() => {
                const lineChartData: MultiLineChartProps['data'] = []
                const barChartData: VerticalBarChartProps['data'] = []
                let barChartStackedData: VerticalBarChartStackedProps['data'] = []

                const dateGroups = getDateGroups(
                    moment(analyticsStartDate),
                    moment(analyticsEndDate),
                    chartInterval
                )
                const entries = dateGroups.map((date) => {
                    const formattedDate = date.format('YYYY-MM-DD')
                    const dateObjects = modelByInterval.get(formattedDate) ?? []
                    return {date: formattedDate, dateObjects}
                })

                const cumulativeSums = {}

                const lastModelDate = moment(
                    Object.keys(groupingIsReversed ? analyticsModelReversed : analyticsModel).at(-1),
                    'YYYY-MM-DD'
                )

                const filteredGroups = selectedSpecific ?
                    mainViewData.filter((g) => g.specificId === selectedSpecific) :
                    mainViewData

                const stackedMap = new Map<string, any>()

                entries.forEach(({date, dateObjects}, index) => {
                    if (selectedGroup['amountOfAddresses'] === 'total' || !selectedSpecific) {
                        lineChartData.push({
                            name: 'Totaal',
                            value: (lineChartData[index - 1]?.value || 0) + sumCountsRecursiveCached(dateObjects),
                            date
                        })
                    }

                    const groupTotals = sumCountsPerKey(dateObjects.flatMap((dateObj) => {
                        const depth: number = Math.max(groupDepths[selectedGroup] - 1, 0)
                        return pluckLevel(dateObj, depth).flatMap((obj) => Object.entries(obj))
                    }))

                    filteredGroups.forEach((group) => {
                        const depthHigher = Math.max(group.modelDepth - 1, 0)

                        let allGroups = dateObjects
                            .flatMap((dateObj) => pluckLevel(dateObj, depthHigher))
                            .flatMap((obj) => Object.entries(obj))

                        const groupValue = groupTotals.get(group.specificId) ?? 0

                        const optionsEntries: [string, any][] = []

                        if (shouldSplitAnalyticsGroup(selectedGroup)) {
                            let newAllGroups = []

                            allGroups.forEach(([optionStr, optionObj]) => {
                                const options = splitAnalyticsGroupValues(optionStr)
                                options.forEach((option) => {
                                    newAllGroups.push([option, optionObj])
                                })
                            })

                            allGroups = newAllGroups
                        }

                        allGroups.forEach(([key, optionObj]) => {
                            if (selectedSpecific && key !== selectedSpecific) {
                                return
                            }
                            const subGroupLevel = (subGroup !== 'total' && subGroup !== '') ? groupingIsReversed ? groupReversedDepths[subGroup] : groupDepths[subGroup] : undefined
                            if (typeof subGroupLevel === 'number' && (selectedGroup !== 'total' || subGroupLevel > 1)) {
                                let subGroupObjects = pluckLevel(optionObj, subGroupLevel - group.modelDepth - (selectedGroup === 'total' ? 2 : 1))

                                if (shouldSplitAnalyticsGroup(subGroup)) {
                                    let newSubGroupObj = []
                                    subGroupObjects.forEach((groupObj) => {
                                        Object.entries(groupObj).forEach(([optionsString, obj]) => {
                                            const options = splitAnalyticsGroupValues(optionsString)
                                            options.forEach((option) => {
                                                newSubGroupObj.push({
                                                    [option]: obj
                                                })
                                            })
                                        })
                                    })

                                    subGroupObjects = newSubGroupObj
                                }

                                subGroupObjects.forEach((subGroupObject) => {
                                    optionsEntries.push(...Object.entries(subGroupObject))
                                })
                            } else {
                                optionsEntries.push([key, optionObj])
                            }
                        })

                        let stacks = [...sumCountsPerKey(optionsEntries)].map(
                            ([key, num]) => ({value: num, key})
                        )
                        stacks.sort((a, b) => b.value - a.value)

                        const previousSum = cumulativeSums[group.name] || 0
                        const newSum = previousSum + groupValue
                        cumulativeSums[group.name] = newSum

                        if (selectedSpecific && chartView === 'trend') {
                            lineChartData.push({
                                name: group.name,
                                value: dateObjects.length === 0 &&
                                    moment(date, 'YYYY-MM-DD').isAfter(lastModelDate) ?
                                    undefined :
                                    newSum,
                                date: date
                            })
                        } else {
                            if (groupBarCharts) {
                                stackedMap.set(date, {
                                    date,
                                    values: stacks
                                })
                            } else {
                                const thisDateIndex = barChartData.findIndex(
                                    (d) => d.date === date
                                )

                                if (thisDateIndex > -1) {
                                    const thisDateDataEntry = barChartData[thisDateIndex]
                                    thisDateDataEntry.value += groupValue
                                } else {
                                    const dataObj = {
                                        value: groupValue,
                                        date
                                    }
                                    if (!(selectedSpecific && subGroup === '')) {
                                        dataObj['top5'] = stacks.slice(0, 5).map((stack) => ({name: stack.key, value: stack.value, href: `/planning?startDate=${date}&endDate=${date}&${selectedGroup['amountOfAddresses']}=${encodeURIComponent(stack.key)}`}))
                                    }
                                    barChartData.push(dataObj)
                                }
                            }
                        }
                    })
                })

                // if (selectedGroup === 'total') {
                //     return (
                //         <MultiLineChart
                //             width={chartContainerWidth}
                //             height={chartContainerHeight}
                //             data={LineChartData}
                //             showGroupList={false}
                //             showDots
                //             applyCurvature={false}
                //             interval={chartInterval}
                //         />
                //     )
                // } else {
                // }
                if (chartView === 'trend') {
                    if (forecast) {
                        const latestDate = dateGroups[dateGroups.length - 1]
                        const forecastEndDate = latestDate
                            .clone()
                            .add(dateGroups.length / 3, chartInterval)
                        const extraDates = getDateGroups(
                            latestDate,
                            forecastEndDate,
                            chartInterval
                        )

                        const dataByName = lineChartData.reduce((acc, item) => {
                            if (!acc[item.name]) acc[item.name] = []
                            acc[item.name].push(item)
                            return acc
                        }, {} as Record<string, typeof lineChartData>)

                        for (const [name, entries] of Object.entries(dataByName)) {
                            const lastPoints = entries.slice(-3)
                            if (lastPoints.length < 2) continue

                            const getY = (entry: (typeof lineChartData)[number]) =>
                                entry.value
                            const getX = (entry: (typeof lineChartData)[number]) =>
                                moment(entry.date).valueOf()

                            const x1 = getX(lastPoints[0])
                            const x2 = getX(lastPoints[2])
                            const y1 = getY(lastPoints[0])
                            const y2 = getY(lastPoints[2])

                            const slope = (y2 - y1) / (x2 - x1)

                            for (const forecastDate of extraDates) {
                                const forecastX = forecastDate.valueOf()
                                const deltaX = forecastX - x2
                                const forecastValue = y2 + slope * deltaX

                                lineChartData.push({
                                    name,
                                    value: forecastValue,
                                    date: forecastDate.format('YYYY-MM-DD'),
                                    forecast: {
                                        min:
                        y2 > forecastValue * 0.9 ? y2 : forecastValue * 0.9,
                                        max: forecastValue * 1.1
                                    }
                                })
                            }
                        }
                    }
                } else {
                    barChartStackedData = Array.from(stackedMap.values())
                    if (groupBarCharts) {
                        const perGroupMap = new Map<string, number>()

                        barChartStackedData.forEach((dateStack) => {
                            dateStack.values.forEach((groupValue) => {
                                const prev = perGroupMap.get(groupValue.key) || 0
                                perGroupMap.set(groupValue.key, prev + groupValue.value)
                            })
                        })

                        const perGroup = Array.from(perGroupMap.entries()).map(([key, value]) => ({
                            key,
                            value
                        }))

                        perGroup.sort((a, b) => b.value - a.value)
                        const first7Groups = perGroup.slice(0, 7)
                        const first7GroupsKeys = first7Groups.map((g) => g.key)
                        barChartStackedData = barChartStackedData.map((stackedData) => {
                            const dateObj: VerticalBarChartStackedProps['data'][0] = {
                                date: stackedData.date,
                                values: []
                            }

                            // first8Groups
                            first7Groups.forEach((group) => {
                                const groupData = stackedData.values.find(
                                    (valueData) => valueData.key === group.key
                                )
                                if (groupData) {
                                    const filterOn = {
                                        customer: 'Klant',
                                        postalCodeZone: 'Adres postcode',
                                        deliveryZone: 'Adres bezorggebied'
                                    }[subGroup || selectedGroup]
                                    if (filterOn) {
                                        groupData.href = `/planning?startDate=${stackedData.date}&endDate=${stackedData.date}&filter=${filterOn}&filterValue=${encodeURIComponent(group.key)}`
                                    }
                                    dateObj.values.push(groupData)
                                }
                            })

                            const other = stackedData.values.filter(
                                (valueData) => !first7GroupsKeys.includes(valueData.key)
                            )
                            const sumOther = other.reduce((sum, valueData) => {
                                return (sum += valueData.value)
                            }, 0)
                            dateObj.values.push({
                                key: 'Overig',
                                value: sumOther
                            })

                            return dateObj
                        })
                    }
                }

                return {
                    lineChartData,
                    barChartData,
                    barChartStackedData
                }
            }, [
                analyticsModel,
                chartView,
                chartInterval,
                groupBarCharts,
                // forecast,
                selectedGroup,
                selectedSpecific,
                subGroup
            ])

            const chart = useMemo(() => {
                if (!chartData) {
                    return null
                }

                if (chartView === 'trend') {
                    return (
                        <MultiLineChart
                            width={chartContainerWidth}
                            height={chartContainerHeight}
                            data={chartData.lineChartData}
                            interval={chartInterval}
                            showGroupList={false}
                        />
                    )
                }

                if (groupBarCharts) {
                    const perGroup: {key: string, value: number}[] = []
                    chartData.barChartStackedData
                        .flatMap((dateStack) => dateStack.values)
                        .forEach((groupValue) => {
                            const groupKeyIndex = perGroup.findIndex(
                                (el) => el.key === groupValue.key
                            )
                            if (groupKeyIndex > -1) {
                                perGroup[groupKeyIndex].value += groupValue.value
                            } else {
                                perGroup.push(structuredClone(groupValue))
                            }
                        })
                    perGroup.sort((a, b) => b.value - a.value)
                    const first7Groups = perGroup.slice(0, 7)
                    const first7GroupsKeys = first7Groups.map((g) => g.key)

                    const colorMap = {}
                    first7GroupsKeys.forEach((group, index) => {
                        const color = legend.find((l) => l.label === group)?.color
                        colorMap[group] = color || legendColorMap[index]
                    })
                    colorMap['Overig'] = legendColorMap[7]

                    return (
                        <VerticalBarChartStacked
                            width={chartContainerWidth}
                            height={chartContainerHeight}
                            data={chartData.barChartStackedData}
                            interval={chartInterval}
                            colorMap={colorMap}
                            history={history}
                        />
                    )
                }

                return (
                    <VerticalBarChart
                        width={chartContainerWidth}
                        height={chartContainerHeight}
                        data={chartData.barChartData}
                        interval={chartInterval}
                        history={history}
                    />
                )
            }, [chartView, chartInterval, history, chartContainerWidth, chartContainerHeight, chartData])

            let title = 'Aantal stops'

            const groupPart = {
                customer: 'per klant',
                messenger: 'per koerier',
                vehicleType: 'per voertuigsoort',
                vehicle: 'per voertuig',
                route: 'per route',
                deliveryZone: 'per bezorggebied',
                postalCodeZone: 'per postcodegebied',
                option: 'per verzendoptie',
                amountOfDaysBetweenHubScanAndCompleted:
        'per aantal dagen tot bezorging'
            }

            if (selectedSpecific) {
                title += ` voor ${selectedSpecific}`
            }

            if (subGroup) {
                title += ` ${groupPart[subGroup]}`
            } else if (!selectedSpecific) {
                title += ` ${groupPart[selectedGroup] || ''}`
            }

            return (
                <div
                    style={{
                        padding: '12px 0',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Card
                        style={{
                            height: '100%',
                            flex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 12
                        }}
                        shadow
                    >
                        <Row style={{alignItems: 'center', justifyContent: 'space-between'}}>
                            {selectedSpecific && (
                                <Button variant='outline' onClick={() => setSelectedSpecific(undefined)} icon='mdi mdi-arrow-left-thick' />
                            )}
                            <H3 style={{textAlign: 'center', marginBottom: 12, flex: 1}}>
                                {title}
                            </H3>
                            <div style={{display: 'flex', alignItems: 'center', columnGap: formSpacing.columnGap, marginBottom: 12}}>
                                <Select
                                    style={{width: 'fit-content', minWidth: 180}}
                                    label='Grafiek weergave'
                                    value={chartView}
                                    onChange={(e) => setChartView(e.target.value as typeof chartView)}
                                    options={[
                                        {
                                            value: 'total',
                                            title: 'Per interval'
                                        },
                                        {
                                            value: 'trend',
                                            title: 'Cumulatief'
                                        }
                                    ]}
                                />
                                <Select
                                    style={{width: 'fit-content', minWidth: 180}}
                                    label='Interval'
                                    value={chartInterval}
                                    onChange={(e) => setChartInterval(e.target.value as typeof chartInterval)}
                                    options={[
                                        {
                                            value: 'day',
                                            title: 'Per dag',
                                            disabled: daysInRange > 100
                                        },
                                        {
                                            value: 'week',
                                            title: 'Per week'
                                        },
                                        {
                                            value: 'month',
                                            title: 'Per maand'
                                        }
                                    ]}
                                />
                            </div>
                        </Row>

                        {/* {chartView === 'total' && (
                                <div>
                                    <Toggle
                                        style={{width: 180}}
                                        label='Toon groepen'
                                        checked={groupBarCharts}
                                        onChange={(e) => setGroupBarCharts(e.target.checked)}
                                    />
                                </div>
                            )} */}
                        {/* {chartView === 'trend' && moment(analyticsEndDate).isAfter(moment()) &&
                                <div>
                                    <Toggle
                                        style={{width: 180}}
                                        label='Prognose'
                                        checked={forecast}
                                        onChange={(e) => setForecast(e.target.checked)}
                                    />
                                </div>
                            } */}
                        <div
                            ref={chartContainerRef}
                            style={{width: '100%', flex: 1, overflow: 'hidden'}}
                        >
                            {!loading && chart}
                        </div>
                    </Card>
                </div>
            )
        }
    },
    {
        title: 'Aantal zendingen',
        icon: 'mdi mdi-package-variant-closed',
        key: 'amountOfParcels',
        subFilterBar: {
            groupOptions: [
                {
                    value: 'total',
                    title: 'Totaal'
                },
                {
                    value: 'customer',
                    title: 'Klant'
                },
                {
                    value: 'carrier',
                    title: 'Vervoerder'
                },
                {
                    value: 'status',
                    title: 'Status'
                },
                {
                    value: 'option',
                    title: 'Verzendoptie'
                },
                {
                    value: 'koppeling',
                    title: 'Koppeling'
                },
                {
                    value: 'amountOfDaysTillDelivery',
                    title: 'Aantal dagen tot bezorging',
                    info: `Aantal dagen tussen status opgehaald en status bezorgd. Wanneer een zending op een afhaalpunt is bezorgd telt dit ook als bezorgd status.

Per vervoerder worden standaard de volgende dagen geteld:

• Veloyd vervoerders – Openingsdagen
• Bpost: ma t/m vr
• Cycloon: ma t/m za
• DHL: ma t/m za
• DHL Europlus: ma t/m vr
• DPD: ma t/m vr
• GLS: ma t/m vr
• Packs: ma t/m zo
• Peddler: ma t/m vr
• PostNL: ma t/m za
• Skynet: ma t/m za

Wanneer een zending met zaterdag- of zondaglevering wordt aangemeld dan wordt deze dag ook geteld.`,
                    infoKeepWhiteSpace: true
                },
                {
                    value: 'country',
                    title: 'Land'
                }
            ]
        },
        leftView: ({loading, subGroup, leftViewData, selectedGroup, groupingIsReversed, legend, selectedSpecific, setSelectedSpecific}) => {
            const chartContainerRef = useRef<HTMLDivElement>(null)
            const [sortDescend, setSortDescend] = useState(true)
            const [chartContainerWidth, setChartContainerWidth] = useState(440)
            const [chartContainerHeight, setChartContainerHeight] = useState<number | null>(null)
            const [search, setSearch] = useState('')
            const [highlightedXTick, setHighlightedXTick] = useState('')
            const [sortOn, setSortOn] = useState<'amount' | 'alphabetic'>(
                'amount'
            )
            // const [subGroup, setSubGroup] = useState('total')

            const amountOfParcelsPerGroupData = useMemo(() => {
                let data = leftViewData
                    .filter((group) => group.amount > 0)
                    .sort((a, b) => b.amount - a.amount)
                    .map((group) => {
                        if (subGroup !== 'total' && subGroup !== '') {
                            const subGroupLevel = groupingIsReversed ? parcelsGroupReversedDepth[subGroup] : parcelsGroupDepth[subGroup]

                            const subGroups = Object.entries(group.obj)
                                .flatMap(([, obj]) => {
                                    let subGroupObj = pluckLevel(
                                        obj,
                                        subGroupLevel - group.modelDepth - 1
                                    )

                                    if (shouldSplitAnalyticsGroup(subGroup)) {
                                        let newSubGroupObj = []
                                        subGroupObj.forEach((groupObj) => {
                                            Object.entries(groupObj).forEach(([optionsString, obj]) => {
                                                const options = splitAnalyticsGroupValues(optionsString)
                                                options.forEach((option) => {
                                                    newSubGroupObj.push({
                                                        [option]: obj
                                                    })
                                                })
                                            })
                                        })

                                        subGroupObj = newSubGroupObj
                                    }

                                    return subGroupObj.flatMap((groupObj) => {
                                        return Object.entries(groupObj).map(
                                            ([key, subGroupObj]) => {
                                                if (subGroup === 'amountOfDaysTillDelivery' && key === 'Niet bezorgd') {
                                                    return undefined
                                                }

                                                return {
                                                    key,
                                                    amount: sumCountsRecursive(subGroupObj)
                                                }
                                            }
                                        ).filter((el) => el)
                                    })
                                })
                                .reduce((subGroups, group) => {
                                    const groupExistingIndex = subGroups.findIndex(
                                        (g) => g.key === group.key
                                    )
                                    if (groupExistingIndex > -1) {
                                        subGroups[groupExistingIndex].amount += group.amount
                                    } else {
                                        subGroups.push(group)
                                    }
                                    return subGroups
                                }, [])
                                .toSorted((a, b) => a.amount - b.amount)

                            return {
                                name: group.name || 'Onbekend',
                                value: group.amount,
                                values: subGroups.map((subGroup) => {
                                    return {
                                        value: subGroup.amount,
                                        key: subGroup.key
                                    }
                                })
                            }
                        } else {
                            let color: undefined | string

                            const legendEntry = legend.find((l) => l.label === group.name)
                            if (legendEntry) {
                                color = legendEntry.color
                            }

                            return {
                                name: group.name || 'Onbekend',
                                value: group.amount,
                                color
                            }
                        }
                    })

                if (subGroup !== 'total' && subGroup !== '') {
                    const allKeys = _.uniq(
                        data.flatMap((group) => group.values.map((v) => v.key))
                    )
                    let allKeysWithSum = allKeys
                        .map((key) => {
                            let sum = 0
                            data.forEach((group) => {
                                group.values.forEach((v) => {
                                    if (v.key === key) {
                                        sum += v.value
                                    }
                                })
                            })
                            return {
                                key,
                                amount: sum
                            }
                        })
                        .toSorted((a, b) => b.amount - a.amount)

                    const restAmount = allKeysWithSum.slice(7).reduce((sum, key) => {
                        sum += key.amount
                        return sum
                    }, 0)
                    allKeysWithSum = [
                        ...allKeysWithSum.slice(0, 7),
                        {key: 'Overig', amount: restAmount}
                    ]

                    const coloredKeys = allKeysWithSum.slice(0, 7).map((k) => k.key)
                    data = data.map((group) => {
                        const newValues = []
                        let restSum = 0
                        group.values.forEach((value) => {
                            if (coloredKeys.includes(value.key)) {
                                newValues.push(value)
                            } else {
                                restSum += value.value
                            }
                        })

                        newValues.sort((a, b) => {
                            const legendIndexA = legend.findIndex((l) => l.label === a.key)
                            const legendIndexB = legend.findIndex((l) => l.label === b.key)
                            return legendIndexA - legendIndexB
                        })

                        if (restSum > 0) {
                            newValues.push({key: 'Overig', value: restSum})
                        }
                        group.values = newValues

                        return group
                    })
                }

                if (sortOn === 'alphabetic') {
                    data = data.sort((a, b) => a.name.localeCompare(b.name))
                } else {
                    if (!sortDescend) {
                        data = data.reverse()
                    }
                }

                return data
            }, [leftViewData, sortDescend, sortOn, subGroup])

            const colorMap = useMemo(() => {
                if (amountOfParcelsPerGroupData[0]?.values) {
                    const allKeys = _.uniq(
                        amountOfParcelsPerGroupData.flatMap((group) =>
                            group.values.map((v) => v.key)
                        )
                    )

                    const result = {}
                    allKeys.forEach((key, index) => {
                        const color = legend.find((l) => l.label === key)?.color
                        result[key] = color || legendColorMap[index] || '#D1D1D1'
                    })
                    return result
                } else {
                    return undefined
                }
            }, [amountOfParcelsPerGroupData])

            useEffect(() => {
                if (search) {
                    const result = amountOfParcelsPerGroupData.filter((group) => {
                        const parts = group.name.split(
                            new RegExp(
                                `(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
                                'gi'
                            )
                        )
                        return parts.some(
                            (part) => part.toLowerCase() === search.toLowerCase()
                        )
                    })
                    if (result.length === 1) {
                        setHighlightedXTick(result[0].name)
                    } else {
                        setHighlightedXTick('')
                    }
                } else {
                    setHighlightedXTick('')
                }
            }, [search])

            useResizeObserver({
                ref: chartContainerRef,
                onResize: (width, height) => {
                    setChartContainerWidth(width)
                    if (!chartContainerHeight) {
                        setChartContainerHeight(height)
                    }
                }
            })

            // let title = 'Aantal zendingen'

            // const groupPart = {
            //     customer: 'per klant',
            //     carrier: 'per vervoerder',
            //     status: 'per status',
            //     option: 'per verzendoptie',
            //     koppeling: 'per koppeling',
            //     amountOfDaysTillDelivery: 'per aantal dagen tot bezorging',
            //     country: 'per land'
            // }

            if (selectedGroup !== 'total') {
                // title += ` ${groupPart[selectedGroup]}`
            }

            if (subGroup) {
                // title += ` ${groupPart[subGroup]}`
            }

            return (
                <div style={{padding: '12px 0', height: '100%'}}>
                    <Card
                        style={{
                            width: '100%',
                            height: '100%',
                            flex: 1,
                            padding: 12,
                            paddingTop: 0,
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        shadow
                    >
                        <div style={{marginBottom: 12}} />
                        <H3 style={{textAlign: 'center', marginBottom: 12}}>Totaal over periode</H3>
                        <div style={{marginBottom: 12}} />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: formSpacing.columnGap,
                                marginBottom: 24,
                                position: 'sticky',
                                top: 0,
                                paddingTop: 12,
                                backgroundColor: Colors.white
                            }}
                        >
                            <IconButton
                                icon={`mdi mdi-sort-${
                                    sortDescend ? 'descending' : 'ascending'
                                }`}
                                onClick={() => setSortDescend(!sortDescend)}
                            />
                            <Select
                                value={sortOn}
                                onChange={(e) => setSortOn(e.target.value as typeof sortOn)}
                                options={[
                                    {
                                        value: 'amount',
                                        title: 'Aantal'
                                    },
                                    {
                                        value: 'alphabetic',
                                        title: 'Alfabetisch'
                                    }
                                ]}
                                label='Sorteer op'
                            />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder='Zoeken'
                                append='mdi mdi-magnify'
                            />
                        </div>
                        <div style={{overflow: 'auto', flex: 1}}>
                            <div style={{minHeight: '100%', maxHeight: '100%'}} ref={chartContainerRef}>
                                {!loading && (
                                    <>
                                        {(subGroup === 'total' || subGroup === '') ?
                                            (
                                                <HorizontalBarChart
                                                    width={chartContainerWidth}
                                                    height={chartContainerHeight}
                                                    barHeight={24}
                                                    data={amountOfParcelsPerGroupData}
                                                    showNameInShortBar={false}
                                                    activeXTick={selectedSpecific}
                                                    onClickXTick={(tick) =>
                                                        setSelectedSpecific(
                                                            selectedSpecific === tick ? undefined : tick
                                                        )
                                                    }
                                                    highlightedXTick={highlightedXTick}
                                                    disableHover={selectedGroup['amountOfParcels'] === 'total'}
                                                />
                                            ) :
                                            (
                                                <HorizontalBarChartStacked
                                                    width={chartContainerWidth}
                                                    height={chartContainerHeight}
                                                    barHeight={24}
                                                    data={amountOfParcelsPerGroupData as HorizontalBarChartStackedProps['data']}
                                                    showNameInShortBar={false}
                                                    showValueInBar={false}
                                                    activeXTick={selectedSpecific}
                                                    onClickXTick={(tick) =>
                                                        setSelectedSpecific(
                                                            selectedSpecific === tick ? undefined : tick
                                                        )
                                                    }
                                                    highlightedXTick={highlightedXTick}
                                                    colorMap={colorMap}
                                                    // isPercentage
                                                    disableHover={selectedGroup['amountOfParcels'] === 'total'}
                                                />
                                            )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            )
        },
        mainView: ({loading, subGroup, selectedGroup, selectedSpecific, setSelectedSpecific, mainViewData, analyticsStartDate, analyticsEndDate, groupingIsReversed, parcelsModel, parcelsModelReversed, legend}) => {
            const [chartView, setChartView] = useState<'total' | 'trend'>(
                'total'
            )

            const daysInRange = useMemo(() => {
                const startDate = moment(analyticsStartDate)
                const endDate = moment(analyticsEndDate)
                return endDate.diff(startDate, 'days')
            }, [analyticsStartDate, analyticsEndDate])

            const [chartInterval, setChartInterval] = useState<
        'day' | 'week' | 'month'
            >('day')
            const chartContainerRef = useRef<HTMLDivElement>(null)
            const [chartContainerWidth, setChartContainerWidth] = useState(1200)
            const [chartContainerHeight, setChartContainerHeight] = useState(700)
            const [forecast] = useState(false)

            const groupBarCharts = useMemo(() => {
                return !!subGroup || (selectedGroup !== 'total' && !selectedSpecific)
            }, [subGroup, selectedGroup, selectedSpecific])

            useResizeObserver({
                ref: chartContainerRef,
                onResize: (width, height) => {
                    setChartContainerWidth(width)
                    setChartContainerHeight(height)
                }
            })

            useEffect(() => {
                if (daysInRange < 32) {
                    setChartInterval('day')
                } else if (daysInRange >= 32 && daysInRange < 100) {
                    setChartInterval('week')
                } else {
                    setChartInterval('month')
                }
            }, [daysInRange])

            const modelByInterval = useMemo(() => {
                const map: Map<string, typeof parcelsModel[number][]> = new Map()

                Object.entries(groupingIsReversed ? parcelsModelReversed : parcelsModel).forEach(([date, obj]) => {
                    const key = moment(date).startOf(chartInterval).format('YYYY-MM-DD')

                    if (!map.has(key)) map.set(key, [])
                    map.get(key).push(obj)
                })

                return map
            }, [parcelsModel, parcelsModelReversed, chartInterval, groupingIsReversed])

            const sumCache = useMemo(() => new WeakMap(), [])

            function sumCountsRecursiveCached(obj: any): number {
                if (!obj) return 0

                const cached = sumCache.get(obj)
                if (cached !== undefined) {
                    return cached
                }

                const value = sumCountsRecursive(obj)
                sumCache.set(obj, value)

                return value
            }

            const chart = useMemo(() => {
                const LineChartData: MultiLineChartProps['data'] = []
                const barChartData: VerticalBarChartProps['data'] = []
                let barChartStackedData: VerticalBarChartStackedProps['data'] = []

                const dateGroups = getDateGroups(
                    moment(analyticsStartDate),
                    moment(analyticsEndDate),
                    chartInterval
                )
                const entries = dateGroups.map((date) => {
                    const formattedDate = date.format('YYYY-MM-DD')
                    const dateObjects = modelByInterval.get(formattedDate) ?? []
                    return {date: formattedDate, dateObjects}
                })

                const cumulativeSums = {}

                const lastModelDate = moment(
                    Object.keys(groupingIsReversed ? parcelsModelReversed : parcelsModel).at(-1),
                    'YYYY-MM-DD'
                )

                const filteredGroups = selectedSpecific ?
                    mainViewData.filter((g) => g.specificId === selectedSpecific) :
                    mainViewData

                const stackedMap = new Map<string, any>()

                entries.forEach(({date, dateObjects}, index) => {
                    if (selectedGroup['amountOfParcels'] === 'total' || !selectedSpecific) {
                        LineChartData.push({
                            name: 'Totaal',
                            value: (LineChartData[index - 1]?.value || 0) + sumCountsRecursiveCached(dateObjects),
                            date
                        })
                    }

                    filteredGroups.forEach((group) => {
                        const depthHigher = Math.max(group.modelDepth - 1, 0)

                        let allGroups = dateObjects
                            .flatMap((dateObj) => pluckLevel(dateObj, depthHigher))
                            .flatMap((obj) => Object.entries(obj))

                        const groupValue = allGroups.reduce(
                            (sum, [groupKey, groupObj]) => {
                                if (shouldSplitAnalyticsGroup(selectedGroup)) {
                                    const keys = splitAnalyticsGroupValues(groupKey)
                                    if (keys.some((key) => key === group.specificId)) {
                                        sum += sumCountsRecursiveCached(groupObj)
                                    }
                                } else if (
                                    groupKey === group.specificId ||
                                    group.specificId === 'total'
                                ) {
                                    sum += sumCountsRecursiveCached(groupObj)
                                }
                                return sum
                            },
                            0
                        )

                        const optionsCounts: {[key: string]: number} = {}

                        if (shouldSplitAnalyticsGroup(selectedGroup)) {
                            let newAllGroups = []

                            allGroups.forEach(([optionStr, optionObj]) => {
                                const options = splitAnalyticsGroupValues(optionStr)
                                options.forEach((option) => {
                                    newAllGroups.push([option, optionObj])
                                })
                            })

                            allGroups = newAllGroups
                        }

                        allGroups.forEach(([key, optionObj]) => {
                            if (selectedSpecific && key !== selectedSpecific) {
                                return
                            }

                            const subGroupLevel = (subGroup !== 'total' && subGroup !== '') ? groupingIsReversed ? parcelsGroupReversedDepth[subGroup] : parcelsGroupDepth[subGroup] : undefined
                            if (typeof subGroupLevel === 'number' && (selectedGroup !== 'total' || subGroupLevel > 1)) {
                                let subGroupObjects = pluckLevel(optionObj, subGroupLevel - group.modelDepth - (selectedGroup === 'total' ? 2 : 1))

                                if (shouldSplitAnalyticsGroup(subGroup)) {
                                    let newSubGroupObj = []
                                    subGroupObjects.forEach((groupObj) => {
                                        Object.entries(groupObj).forEach(([optionsString, obj]) => {
                                            const options = splitAnalyticsGroupValues(optionsString)
                                            options.forEach((option) => {
                                                newSubGroupObj.push({
                                                    [option]: obj
                                                })
                                            })
                                        })
                                    })

                                    subGroupObjects = newSubGroupObj
                                }

                                subGroupObjects.forEach((subGroupObject) => {
                                    Object.entries(subGroupObject).forEach(([subGroupKey, subGroupObj]) => {
                                        if (subGroup === 'amountOfDaysTillDelivery' && subGroupKey === 'Niet bezorgd') {
                                            return
                                        }

                                        const value = sumCountsRecursiveCached(subGroupObj)
                                        if (typeof optionsCounts[subGroupKey] === 'number') {
                                            optionsCounts[subGroupKey] += value
                                        } else {
                                            optionsCounts[subGroupKey] = value
                                        }
                                    })
                                })
                            } else {
                                const value = sumCountsRecursiveCached(optionObj)
                                if (typeof optionsCounts[key] === 'number') {
                                    optionsCounts[key] = optionsCounts[key] + value
                                } else {
                                    optionsCounts[key] = value
                                }
                            }
                        })

                        let stacks = Object.entries(optionsCounts).map(
                            ([key, num]) => ({value: num, key})
                        )
                        stacks.sort((a, b) => b.value - a.value)

                        const previousSum = cumulativeSums[group.name] || 0
                        const newSum = previousSum + groupValue
                        cumulativeSums[group.name] = newSum

                        if (selectedSpecific && chartView === 'trend') {
                            LineChartData.push({
                                name: group.name,
                                value:
                                    dateObjects.length === 0 &&
                                    moment(date, 'YYYY-MM-DD').isAfter(lastModelDate) ?
                                        undefined :
                                        newSum,
                                date: date
                            })
                        } else {
                            if (groupBarCharts) {
                                stackedMap.set(date, {
                                    date,
                                    values: stacks
                                })
                            } else {
                                const thisDateIndex = barChartData.findIndex(
                                    (d) => d.date === date
                                )

                                if (thisDateIndex > -1) {
                                    const thisDateDataEntry = barChartData[thisDateIndex]
                                    thisDateDataEntry.value += groupValue
                                } else {
                                    barChartData.push({
                                        value: groupValue,
                                        date
                                    })
                                }
                            }
                        }
                    })
                })

                if (chartView === 'trend') {
                    if (forecast) {
                        const latestDate = dateGroups[dateGroups.length - 1]
                        const forecastEndDate = latestDate
                            .clone()
                            .add(dateGroups.length / 3, chartInterval)
                        const extraDates = getDateGroups(
                            latestDate,
                            forecastEndDate,
                            chartInterval
                        )

                        const dataByName = LineChartData.reduce((acc, item) => {
                            if (!acc[item.name]) acc[item.name] = []
                            acc[item.name].push(item)
                            return acc
                        }, {} as Record<string, typeof LineChartData>)

                        for (const [name, entries] of Object.entries(dataByName)) {
                            const lastPoints = entries.slice(-3)
                            if (lastPoints.length < 2) continue

                            const getY = (entry: (typeof LineChartData)[number]) =>
                                entry.value
                            const getX = (entry: (typeof LineChartData)[number]) =>
                                moment(entry.date).valueOf()

                            const x1 = getX(lastPoints[0])
                            const x2 = getX(lastPoints[2])
                            const y1 = getY(lastPoints[0])
                            const y2 = getY(lastPoints[2])

                            const slope = (y2 - y1) / (x2 - x1)

                            for (const forecastDate of extraDates) {
                                const forecastX = forecastDate.valueOf()
                                const deltaX = forecastX - x2
                                const forecastValue = y2 + slope * deltaX

                                LineChartData.push({
                                    name,
                                    value: forecastValue,
                                    date: forecastDate.format('YYYY-MM-DD'),
                                    forecast: {
                                        min:
                                            y2 > forecastValue * 0.9 ? y2 : forecastValue * 0.9,
                                        max: forecastValue * 1.1
                                    }
                                })
                            }
                        }
                    }
                } else {
                    barChartStackedData = Array.from(stackedMap.values())
                    if (groupBarCharts) {
                        const perGroup: {key: string, value: number}[] = []
                        barChartStackedData
                            .flatMap((dateStack) => dateStack.values)
                            .forEach((groupValue) => {
                                const groupKeyIndex = perGroup.findIndex(
                                    (el) => el.key === groupValue.key
                                )
                                if (groupKeyIndex > -1) {
                                    perGroup[groupKeyIndex].value += groupValue.value
                                } else {
                                    perGroup.push(structuredClone(groupValue))
                                }
                            })
                        perGroup.sort((a, b) => b.value - a.value)
                        const first8Groups = perGroup.slice(0, 8).filter((group) => legend.some((l) => l.label === group.key))
                        const first8GroupsKeys = first8Groups.map((g) => g.key)
                        barChartStackedData = barChartStackedData.map((stackedData) => {
                            const dateObj: VerticalBarChartStackedProps['data'][0] = {
                                date: stackedData.date,
                                values: []
                            }

                            // first8Groups
                            first8Groups.forEach((group) => {
                                const groupData = stackedData.values.find(
                                    (valueData) => valueData.key === group.key
                                )
                                if (groupData) {
                                    dateObj.values.push(groupData)
                                }
                            })

                            const other = stackedData.values.filter(
                                (valueData) => !first8GroupsKeys.includes(valueData.key)
                            )
                            const sumOther = other.reduce((sum, valueData) => {
                                return (sum += valueData.value)
                            }, 0)
                            dateObj.values.push({
                                key: 'Overig',
                                value: sumOther
                            })

                            return dateObj
                        })
                    }
                }

                return {
                    LineChartData,
                    barChartData,
                    barChartStackedData
                }
            }, [
                parcelsModel,
                parcelsModelReversed,
                chartView,
                chartInterval,
                groupBarCharts,
                selectedGroup,
                selectedSpecific,
                subGroup,
                groupingIsReversed
            ])

            const chartComponent = useMemo(() => {
                if (!chart) {
                    return null
                }

                if (chartView === 'trend') {
                    return (
                        <MultiLineChart
                            width={chartContainerWidth}
                            height={chartContainerHeight}
                            data={chart.LineChartData}
                            interval={chartInterval}
                            showGroupList={false}
                        />
                    )
                }

                if (groupBarCharts) {
                    const perGroup: {key: string, value: number}[] = []
                    chart.barChartStackedData
                        .flatMap((dateStack) => dateStack.values)
                        .forEach((groupValue) => {
                            const groupKeyIndex = perGroup.findIndex(
                                (el) => el.key === groupValue.key
                            )
                            if (groupKeyIndex > -1) {
                                perGroup[groupKeyIndex].value += groupValue.value
                            } else {
                                perGroup.push(structuredClone(groupValue))
                            }
                        })
                    perGroup.sort((a, b) => b.value - a.value)
                    const first8Groups = perGroup.slice(0, 8).filter((group) => legend.some((l) => l.label === group.key))
                    const first8GroupsKeys = first8Groups.map((g) => g.key)

                    const colorMap = {}
                    first8GroupsKeys.forEach((group, index) => {
                        const color = legend.find((l) => l.label === group)?.color
                        colorMap[group] = color || legendColorMap[index]
                    })
                    colorMap['Overig'] = legendColorMap[7]

                    return (
                        <VerticalBarChartStacked
                            width={chartContainerWidth}
                            height={chartContainerHeight}
                            data={chart.barChartStackedData}
                            colorMap={colorMap}
                            interval={chartInterval}
                        />
                    )
                } else {
                    return (
                        <VerticalBarChart
                            width={chartContainerWidth}
                            height={chartContainerHeight}
                            data={chart.barChartData}
                            interval={chartInterval}
                        />
                    )
                }
            }, [
                chart,
                chartView,
                chartInterval,
                groupBarCharts,
                legend,
                legendColorMap,
                chartContainerWidth,
                chartContainerHeight
            ])

            let title = 'Aantal zendingen'

            const groupPart = {
                customer: 'per klant',
                carrier: 'per vervoerder',
                status: 'per status',
                option: 'per verzendoptie',
                koppeling: 'per koppeling',
                amountOfDaysTillDelivery: 'per aantal dagen tot bezorging',
                country: 'per land'
            }

            if (selectedSpecific) {
                title += ` voor ${selectedSpecific}`
            }

            if (subGroup) {
                title += ` ${groupPart[subGroup]}`
            } else if (!selectedSpecific) {
                title += ` ${groupPart[selectedGroup] || ''}`
            }

            return (
                <div
                    style={{
                        padding: '12px 0',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Card
                        style={{
                            flex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 12
                        }}
                        shadow
                    >
                        <Row style={{alignItems: 'center', justifyContent: 'space-between'}}>
                            {selectedSpecific && (
                                <Button variant='outline' onClick={() => setSelectedSpecific(undefined)} icon='mdi mdi-arrow-left-thick' />
                            )}
                            <H3 style={{textAlign: 'center', marginBottom: 12, flex: 1}}>
                                {title}
                            </H3>
                            <div style={{display: 'flex', alignItems: 'center', columnGap: formSpacing.columnGap, marginBottom: 12}}>
                                <Select
                                    style={{width: 'fit-content', minWidth: 180}}
                                    label='Grafiek weergave'
                                    value={chartView}
                                    onChange={(e) => setChartView(e.target.value as typeof chartView)}
                                    options={[
                                        {
                                            value: 'total',
                                            title: 'Per interval'
                                        },
                                        {
                                            value: 'trend',
                                            title: 'Cumulatief'
                                        }
                                    ]}
                                />
                                <Select
                                    style={{width: 'fit-content', minWidth: 180}}
                                    label='Interval'
                                    value={chartInterval}
                                    onChange={(e) => setChartInterval(e.target.value as typeof chartInterval)}
                                    options={[
                                        {
                                            value: 'day',
                                            title: 'Per dag',
                                            disabled: daysInRange > 100
                                        },
                                        {
                                            value: 'week',
                                            title: 'Per week'
                                        },
                                        {
                                            value: 'month',
                                            title: 'Per maand'
                                        }
                                    ]}
                                />
                            </div>
                        </Row>
                        {/* {chartView === 'trend' &&
                                <div>
                                    <Toggle
                                        style={{width: 180}}
                                        label='Prognose'
                                        checked={forecast}
                                        onChange={(e) => setForecast(e.target.checked)}
                                    />
                                </div>
                            } */}
                        <div
                            ref={chartContainerRef}
                            style={{width: '100%', flex: 1, overflow: 'hidden'}}
                        >
                            {!loading && chartComponent}
                        </div>
                    </Card>
                </div>
            )
        }
    },
    {
        title: 'Percentage aangeboden',
        icon: 'mdi mdi-offer',
        key: 'amountOfDeliveryAttempts',
        subFilterBar: {
            groupOptions: [
                {
                    value: 'total',
                    title: 'Totaal'
                },
                {
                    value: 'customer',
                    title: 'Klant'
                },
                {
                    value: 'messenger',
                    title: 'Koerier'
                },
                {
                    value: 'vehicleType',
                    title: 'Voertuigsoort'
                },
                {
                    value: 'vehicle',
                    title: 'Voertuig'
                },
                {
                    value: 'route',
                    title: 'Routenaam'
                },
                {
                    value: 'deliveryZone',
                    title: 'Bezorggebied'
                },
                {
                    value: 'postalCodeZone',
                    title: 'Postcodegebied'
                }
            ]
        },
        leftView: ({loading, leftViewData, selectedGroup, selectedSpecific, setSelectedSpecific}) => {
            const chartContainerRef = useRef<HTMLDivElement>(null)
            const [sortDescend, setSortDescend] = useState(true)
            const [chartContainerWidth, setChartContainerWidth] = useState(440)
            const [chartContainerHeight, setChartContainerHeight] = useState<number | null>(null)
            const [search, setSearch] = useState('')
            const [highlightedXTick, setHighlightedXTick] = useState('')
            const [sortOn, setSortOn] = useState<'percentage_delivered' | 'percentage_offered' | 'amount_delivered' | 'amount_offered' | 'alphabetic'>(
                'amount_offered'
            )

            const horizontalBarChartStackedData: HorizontalBarChartStackedProps['data'] = useMemo(() => {
                let data: HorizontalBarChartStackedProps['data'] = leftViewData
                    .filter((group) => group.amount > 0)
                    .sort((a, b) => {
                        if (sortOn === 'percentage_delivered') {
                            if (a.deliveryAttemptsPercentages && b.deliveryAttemptsPercentages) {
                                return (
                                    b.deliveryAttemptsPercentages.delivered -
                                    a.deliveryAttemptsPercentages.delivered
                                )
                            }
                        }

                        if (sortOn === 'percentage_offered') {
                            if (a.deliveryAttemptsPercentages && b.deliveryAttemptsPercentages) {
                                return (
                                    b.deliveryAttemptsPercentages.offered -
                                    a.deliveryAttemptsPercentages.offered
                                )
                            }
                        }

                        if (sortOn === 'amount_delivered') {
                            if (a.deliveryAttempts && b.deliveryAttempts) {
                                return b.deliveryAttempts.delivered - a.deliveryAttempts.delivered
                            }
                        }

                        if (sortOn === 'amount_offered') {
                            if (a.deliveryAttempts && b.deliveryAttempts) {
                                return b.deliveryAttempts.offered - a.deliveryAttempts.offered
                            }
                        }

                        if (sortOn === 'alphabetic') {
                            return a.name.localeCompare(b.name)
                        }

                        return b.amount - a.amount
                    })
                    .map((group) => ({
                        name: group.name || 'Onbekend',
                        values: [
                            {
                                value: group.deliveryAttemptsPercentages?.delivered || 0,
                                key: 'delivered',
                                title: typeof group.deliveryAttempts?.delivered === 'number' ? `Aantal bezorgd: ${group.deliveryAttempts.delivered}` : ''
                            },
                            {
                                value: group.deliveryAttemptsPercentages?.offered || 0,
                                key: 'offered',
                                title: typeof group.deliveryAttempts?.offered === 'number' ? `Aantal aangeboden: ${group.deliveryAttempts.offered}` : ''
                            }
                        ]
                    }))

                if (!sortDescend) {
                    data = data.reverse()
                }

                return data
            }, [leftViewData, sortOn, sortDescend])

            useEffect(() => {
                if (search) {
                    const result = horizontalBarChartStackedData.filter((group) => {
                        const parts = group.name.split(
                            new RegExp(
                                `(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
                                'gi'
                            )
                        )
                        return parts.some(
                            (part) => part.toLowerCase() === search.toLowerCase()
                        )
                    })
                    if (result.length === 1) {
                        setHighlightedXTick(result[0].name)
                    } else {
                        setHighlightedXTick('')
                    }
                } else {
                    setHighlightedXTick('')
                }
            }, [search])

            useResizeObserver({
                ref: chartContainerRef,
                onResize: (width, height) => {
                    setChartContainerWidth(width)
                    if (!chartContainerHeight) {
                        setChartContainerHeight(height)
                    }
                }
            })

            // let title = 'Aangeboden & bezorgd'

            // const groupPart = {
            //     customer: 'per klant',
            //     messenger: 'per koerier',
            //     vehicleType: 'per voertuigsoort',
            //     vehicle: 'per voertuig',
            //     route: 'per routenaam',
            //     deliveryZone: 'per bezorggebied',
            //     postalCodeZone: 'per postcodegebied'
            // }

            if (selectedGroup !== 'total') {
                // title += ` ${groupPart[selectedGroup]}`
            }

            return (
                <div style={{padding: '12px 0', height: '100%'}}>
                    <Card
                        style={{
                            width: '100%',
                            height: '100%',
                            flex: 1,
                            padding: 12,
                            paddingTop: 0,
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        shadow
                    >
                        <div style={{marginBottom: 12}} />
                        <H3 style={{textAlign: 'center', marginBottom: 12}}>Totaal over periode</H3>
                        <div style={{marginBottom: 12}} />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: formSpacing.columnGap,
                                marginBottom: 24,
                                position: 'sticky',
                                top: 0,
                                paddingTop: 12,
                                backgroundColor: Colors.white
                            }}
                        >
                            <IconButton
                                icon={`mdi mdi-sort-${
                                    sortDescend ? 'descending' : 'ascending'
                                }`}
                                onClick={() => setSortDescend(!sortDescend)}
                            />
                            <Select
                                value={sortOn}
                                onChange={(e) => setSortOn(e.target.value as typeof sortOn)}
                                options={[
                                    {
                                        value: 'amount_offered',
                                        title: 'Aantal aangeboden',
                                        info: 'De groepen worden gesorteerd op het aantal stops wat is aangeboden'
                                    },
                                    {
                                        value: 'amount_delivered',
                                        title: 'Aantal bezorgd',
                                        info: 'De groepen worden gesorteerd op het aantal stops wat is bezorgd'
                                    },
                                    {
                                        value: 'percentage_offered',
                                        title: 'Percentage aangeboden'
                                    },
                                    {
                                        value: 'percentage_delivered',
                                        title: 'Percentage bezorgd'
                                    },
                                    {
                                        value: 'alphabetic',
                                        title: 'Alfabetisch'
                                    }
                                ]}
                                label='Sorteer op'
                            />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder='Zoeken'
                                append='mdi mdi-magnify'
                            />
                        </div>
                        <div style={{overflow: 'auto', flex: 1}}>
                            <div style={{minHeight: '100%', maxHeight: '100%'}} ref={chartContainerRef}>
                                {!loading && (
                                    <HorizontalBarChartStacked
                                        width={chartContainerWidth}
                                        height={chartContainerHeight}
                                        barHeight={24}
                                        data={horizontalBarChartStackedData}
                                        showNameInShortBar={false}
                                        activeXTick={selectedSpecific}
                                        onClickXTick={(tick) =>
                                            setSelectedSpecific(
                                                selectedSpecific === tick ? undefined : tick
                                            )
                                        }
                                        colorMap={{
                                            delivered: '#3077B6',
                                            offered: '#95C0DE'
                                        }}
                                        isPercentage
                                        highlightedXTick={highlightedXTick}
                                        combineStackTitles
                                        disableHover={selectedGroup['amountOfDeliveryAttempts'] === 'total'}
                                    />
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            )
        },
        mainView: ({loading, mainViewData, analyticsStartDate, analyticsEndDate, selectedSpecific, setSelectedSpecific, stopsModel, selectedGroup}) => {
            const [chartView, setChartView] = useState<'total' | 'trend'>(
                'trend'
            )

            const daysInRange = useMemo(() => {
                const startDate = moment(analyticsStartDate)
                const endDate = moment(analyticsEndDate)
                return endDate.diff(startDate, 'days')
            }, [analyticsStartDate, analyticsEndDate])

            const [chartInterval, setChartInterval] = useState<
        'day' | 'week' | 'month'
            >('day')
            const chartContainerRef = useRef<HTMLDivElement>(null)
            const [chartContainerWidth, setChartContainerWidth] = useState(1200)
            const [chartContainerHeight, setChartContainerHeight] = useState(700)

            const amountOfAddressesPerGroupData = useMemo(() => {
                let data = mainViewData
                    .filter((group) => group.amount > 0)
                    .sort((a, b) => b.amount - a.amount)
                    .map((group) => ({
                        name: group.name,
                        value: group.amount
                    }))

                return data
            }, [mainViewData, chartView])

            useResizeObserver({
                ref: chartContainerRef,
                onResize: (width, height) => {
                    setChartContainerWidth(width)
                    setChartContainerHeight(height)
                }
            })

            useEffect(() => {
                if (daysInRange < 32) {
                    setChartInterval('day')
                } else if (daysInRange >= 32 && daysInRange < 100) {
                    setChartInterval('week')
                } else {
                    setChartInterval('month')
                }
            }, [daysInRange])

            const colorMap = {
                Aangeboden: '#B7D2E9',
                Bezorgd: '#3077B6'
            }

            const chart = useMemo(() => {
                const dateGroups = getDateGroups(
                    moment(analyticsStartDate),
                    moment(analyticsEndDate),
                    chartInterval
                )
                const entries = dateGroups.map((date) => {
                    const formattedDate = date.format('YYYY-MM-DD')
                    const dateObjects = Object.entries(stopsModel)
                        .filter(([modelDate]) =>
                            moment(modelDate).isSame(date, chartInterval)
                        )
                        .map(([, dateObj]) => dateObj)
                    return {date: formattedDate, dateObjects}
                })

                if (chartView === 'trend') {
                    const offeredLineData: MultiLineChartProps['data'] = entries.map(
                        ({date, dateObjects}) => {
                            const isCompletedLevel = groupStopsDepth['isCompleted']
                            let isCompletedSum = 0
                            let totalSum = 0

                            mainViewData.filter((group) => selectedSpecific ? group.specificId === selectedSpecific : true).forEach((group) => {
                                const depthHigher = Math.max(group.modelDepth - 1, 0)

                                const allGroups = dateObjects
                                    .flatMap((dateObj) => pluckLevel(dateObj, depthHigher))
                                    .flatMap((obj) => Object.entries(obj))

                                allGroups.forEach(([groupKey, groupObj]) => {
                                    const groupKeyMatches = shouldSplitAnalyticsGroup(selectedGroup) ?
                                        splitAnalyticsGroupValues(groupKey).some((k) => k === group.specificId) :
                                        groupKey === group.specificId

                                    if (groupKeyMatches || group.specificId === 'total') {
                                        const depth = isCompletedLevel - depthHigher - 2
                                        const pluckedCompleted = pluckLevel(
                                            groupObj,
                                            depth
                                        )

                                        pluckedCompleted.forEach((obj) => {
                                            Object.entries(obj).forEach(([isCompleted, obj]) => {
                                                if (isCompleted === 'true') {
                                                    isCompletedSum += sumCountsRecursive(obj)
                                                }
                                            })
                                        })

                                        totalSum += sumCountsRecursive(groupObj)
                                    }
                                })
                            })

                            // dateObjects.forEach((obj) => {
                            //     const pluckedCompleted = pluckLevel(
                            //         obj,
                            //         isCompletedLevel - 1
                            //     )
                            //     pluckedCompleted.forEach((obj) => {
                            //         Object.entries(obj).forEach(([isCompleted, obj]) => {
                            //             if (isCompleted === 'true') {
                            //                 isCompletedSum += sumCountsRecursive(obj)
                            //             }
                            //         })
                            //     })

                            //     totalSum += sumCountsRecursive(obj)
                            // })

                            return {
                                name: 'Aangeboden',
                                value: isCompletedSum === 0 ?
                                    0 :
                                    parseFloat(
                                        ((isCompletedSum / totalSum) * 100).toFixed(2)
                                    ),
                                date,
                                skip: totalSum === 0
                            }
                        }
                    ).filter(({skip}) => !skip)

                    const deliveredLineData: MultiLineChartProps['data'] = entries.map(
                        ({date, dateObjects}) => {
                            const isDeliveredLevel = groupStopsDepth['isDelivered']
                            let isDeliveredSum = 0
                            let totalSum = 0

                            mainViewData.filter((group) => selectedSpecific ? group.specificId === selectedSpecific : true).forEach((group) => {
                                const depthHigher = Math.max(group.modelDepth - 1, 0)

                                const allGroups = dateObjects
                                    .flatMap((dateObj) => pluckLevel(dateObj, depthHigher))
                                    .flatMap((obj) => Object.entries(obj))

                                allGroups.forEach(([groupKey, groupObj]) => {
                                    const groupKeyMatches = shouldSplitAnalyticsGroup(selectedGroup) ?
                                        splitAnalyticsGroupValues(groupKey).some((k) => k === group.specificId) :
                                        groupKey === group.specificId

                                    if (groupKeyMatches || group.specificId === 'total') {
                                        const depth = isDeliveredLevel - depthHigher - 2
                                        const pluckedCompleted = pluckLevel(
                                            groupObj,
                                            depth
                                        )
                                        pluckedCompleted.forEach((obj) => {
                                            Object.entries(obj).forEach(([isDelivered, obj]) => {
                                                if (isDelivered === 'true') {
                                                    isDeliveredSum += sumCountsRecursive(obj)
                                                }
                                            })
                                        })

                                        totalSum += sumCountsRecursive(groupObj)
                                    }
                                })
                            })

                            // dateObjects.forEach((obj) => {
                            //     const pluckedDelivered = pluckLevel(
                            //         obj,
                            //         isDeliveredLevel - 1
                            //     )
                            //     pluckedDelivered.forEach((obj) => {
                            //         Object.entries(obj).forEach(([isDelivered, obj]) => {
                            //             if (isDelivered === 'true') {
                            //                 isDeliveredSum += sumCountsRecursive(obj)
                            //             }
                            //         })
                            //     })

                            //     totalSum += sumCountsRecursive(obj)
                            // })

                            return {
                                name: 'Bezorgd',
                                value: isDeliveredSum === 0 ?
                                    0 :
                                    parseFloat(
                                        ((isDeliveredSum / totalSum) * 100).toFixed(2)
                                    ),
                                date,
                                skip: totalSum === 0
                            }
                        }
                    ).filter(({skip}) => !skip)

                    const lines = [...offeredLineData, ...deliveredLineData]
                    // const min = Math.min(...lines.map(({value}) => value))
                    // const max = Math.max(...lines.map(({value}) => value))
                    // let minimumY = Math.floor(min / 10) * 10
                    // minimumY = Math.min(minimumY, 90)
                    // const steps = Math.floor((max - minimumY) / 5) + 1

                    return (
                        <MultiLineChart
                            width={chartContainerWidth}
                            height={chartContainerHeight}
                            data={lines}
                            colorMap={colorMap}
                            interval={chartInterval}
                            showDots
                            applyCurvature={false}
                            showGroupList={false}
                            // minimumY={0}
                            maximumY={100}
                            // amountYTicks={isFinite(steps) ? steps : undefined}
                            appendValueLabel='%'
                        />
                    )
                }

                if (chartView === 'total') {
                    const barChartData: VerticalBarChartStackedProps['data'] = entries.map(
                        ({date, dateObjects}) => {
                            let totalSum = 0

                            const isCompletedLevel = groupStopsDepth['isCompleted']
                            let isCompletedSum = 0

                            const isDeliveredLevel = groupStopsDepth['isDelivered']
                            let isDeliveredSum = 0

                            dateObjects.forEach((obj) => {
                                const pluckedCompleted = pluckLevel(
                                    obj,
                                    isCompletedLevel - 1
                                )
                                pluckedCompleted.forEach((obj) => {
                                    Object.entries(obj).forEach(([isCompleted, obj]) => {
                                        if (isCompleted === 'true') {
                                            isCompletedSum += sumCountsRecursive(obj)
                                        }
                                    })
                                })

                                const pluckedDelivered = pluckLevel(
                                    obj,
                                    isDeliveredLevel - 1
                                )
                                pluckedDelivered.forEach((obj) => {
                                    Object.entries(obj).forEach(([isDelivered, obj]) => {
                                        if (isDelivered === 'true') {
                                            isDeliveredSum += sumCountsRecursive(obj)
                                        }
                                    })
                                })

                                totalSum += sumCountsRecursive(obj)
                            })

                            const percentageDelivered = isDeliveredSum / totalSum * 100
                            const percentageCompleted = isCompletedSum / totalSum * 100

                            const percentageCompletedDifference = Math.max(0, percentageCompleted - percentageDelivered)

                            return {
                                values: [
                                    {
                                        value: parseFloat(percentageDelivered.toFixed(2)),
                                        key: 'Bezorgd'
                                    },
                                    {
                                        value: parseFloat(percentageCompletedDifference.toFixed(2)),
                                        key: 'Aangeboden',
                                        valueLabel: parseFloat(percentageCompleted.toFixed(2))
                                    }
                                ],
                                date
                            }
                        }
                    )

                    const colorMap = {
                        Bezorgd: '#3077B6',
                        Aangeboden: '#95C0DE'
                    }

                    return (
                        <VerticalBarChartStacked
                            width={chartContainerWidth}
                            height={chartContainerHeight}
                            data={barChartData}
                            colorMap={colorMap}
                            maximumY={100}
                            dataSuffix='%'
                        />
                    )
                }
            }, [
                amountOfAddressesPerGroupData,
                chartView,
                chartContainerWidth,
                chartContainerHeight,
                chartInterval,
                selectedSpecific
            ])

            let title = 'Aangeboden & bezorgd'

            // const groupPart = {
            //     customer: 'per klant',
            //     messenger: 'per koerier',
            //     vehicleType: 'per voertuigsoort',
            //     vehicle: 'per voertuig',
            //     route: 'per route',
            //     deliveryZone: 'per bezorggebied',
            //     postalCodeZone: 'per postcodegebied'
            // }

            if (selectedGroup !== 'total') {
                if (selectedSpecific) {
                    title += ` voor ${selectedSpecific}`
                } else {
                    // title += ` ${groupPart[selectedGroup['amountOfDeliveryAttempts']]}`
                }
            }

            return (
                <div
                    style={{
                        padding: '12px 0',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Card
                        style={{
                            flex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 12
                        }}
                        shadow
                    >
                        <Row style={{justifyContent: 'space-between', alignItems: 'center'}}>
                            {selectedSpecific && (
                                <Button variant='outline' onClick={() => setSelectedSpecific(undefined)} icon='mdi mdi-arrow-left-thick' />
                            )}
                            <H3 style={{flex: 1, textAlign: 'center', marginBottom: 12}}>
                                {title}
                            </H3>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    columnGap: formSpacing.columnGap,
                                    marginBottom: 12
                                }}
                            >
                                {selectedGroup !== 'total' && (
                                    <Select
                                        style={{width: 'fit-content', minWidth: 180}}
                                        label='Grafiek weergave'
                                        value={chartView}
                                        onChange={(e) => setChartView(e.target.value as typeof chartView)}
                                        options={[
                                            {
                                                value: 'total',
                                                title: 'Per interval'
                                            },
                                            {
                                                value: 'trend',
                                                title: 'Trendlijn'
                                            }
                                        ]}
                                    />
                                )}
                                <Select
                                    style={{width: 'fit-content', minWidth: 180}}
                                    label='Interval'
                                    value={chartInterval}
                                    onChange={(e) => setChartInterval(e.target.value as typeof chartInterval)}
                                    options={[
                                        {
                                            value: 'day',
                                            title: 'Per dag',
                                            disabled: daysInRange > 100
                                        },
                                        {
                                            value: 'week',
                                            title: 'Per week'
                                        },
                                        {
                                            value: 'month',
                                            title: 'Per maand'
                                        }
                                    ]}
                                />
                            </div>
                        </Row>
                        <div
                            ref={chartContainerRef}
                            style={{width: '100%', flex: 1, overflow: 'hidden'}}
                        >
                            {!loading && chart}
                        </div>
                    </Card>
                </div>
            )
        }
    },
    {
        title: 'Afwijking routeduur',
        icon: 'mdi mdi-flag-checkered',
        key: 'differenceRouteDuration',
        subFilterBar: {
            groupOptions: [
                {
                    value: 'messenger',
                    title: 'Koerier'
                },
                {
                    value: 'vehicleType',
                    title: 'Voertuigsoort'
                },
                {
                    value: 'vehicle',
                    title: 'Voertuig'
                },
                {
                    value: 'route',
                    title: 'Routenaam'
                }
            ]
        },
        leftView: ({loading, selectedGroup, subFilterBar, leftViewData, selectedSpecific, setSelectedSpecific}) => {
            const chartContainerRef = useRef<HTMLDivElement>(null)
            const [sortDescend, setSortDescend] = useState(true)
            const [chartContainerWidth, setChartContainerWidth] = useState(440)
            const [chartContainerHeight, setChartContainerHeight] = useState<number | null>(null)
            const [search, setSearch] = useState('')
            const [highlightedXTick, setHighlightedXTick] = useState('')
            const [sortOn, setSortOn] = useState<'amount' | 'amount_completed' | 'alphabetic'>(
                'amount_completed'
            )

            useEffect(() => {
                if (!subFilterBar?.groupOptions?.map((option) => option.value).includes(selectedGroup)) {
                    setSelectedGroup('differenceRouteDuration', 'messenger')
                }
            }, [selectedGroup])

            const [differenceInRouteDurationPerGroupData, differenceInRouteDurationPerGroupDivergingData] = useMemo(() => {
                let data = leftViewData.sort((a, b) => {
                    if (sortOn === 'amount') {
                        if (typeof a.differenceInRouteDuration === 'number' && typeof b.differenceInRouteDuration === 'number') {
                            return a.differenceInRouteDuration - b.differenceInRouteDuration
                        } else {
                            return 0
                        }
                    }

                    if (sortOn === 'amount_completed') {
                        if (typeof a.amount === 'number' && typeof b.amount === 'number') {
                            return b.amount - a.amount
                        } else {
                            return 0
                        }
                    }

                    if (sortOn === 'alphabetic') {
                        return a.name.localeCompare(b.name)
                    }

                    return b.differenceInRouteDuration - a.differenceInRouteDuration
                }).map((group) => ({
                    name: group.name === 'unknown' ? 'Onbekend' : group.name || 'Onbekend',
                    value: Math.round(group.differenceInRouteDuration) || 0
                }))

                if (!sortDescend) {
                    data = data.reverse()
                }

                const normalData = data.filter((d) => d.value <= 0).map((d) => ({...d, value: Math.abs(d.value)}))
                const divergingData = data.filter((d) => d.value > 0)

                return [normalData, divergingData]
            }, [leftViewData, sortDescend, selectedGroup, sortOn])

            useEffect(() => {
                if (search) {
                    const combinedNames = [differenceInRouteDurationPerGroupData.map((d) => d.name), differenceInRouteDurationPerGroupDivergingData.map((d) => d.name)].flat()
                    const result = combinedNames.filter((name) => {
                        const parts = name.split(
                            new RegExp(
                                `(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
                                'gi'
                            )
                        )
                        return parts.some(
                            (part) => part.toLowerCase() === search.toLowerCase()
                        )
                    })
                    if (result.length === 1) {
                        setHighlightedXTick(result[0])
                    } else {
                        setHighlightedXTick('')
                    }
                } else {
                    setHighlightedXTick('')
                }
            }, [search])

            useResizeObserver({
                ref: chartContainerRef,
                onResize: (width, height) => {
                    setChartContainerWidth(width)
                    if (!chartContainerHeight) {
                        setChartContainerHeight(height)
                    }
                }
            })

            // let title = `Afwijking routeduur`

            // const groupPart = {
            //     messenger: 'per koerier',
            //     vehicleType: 'per voertuigsoort',
            //     vehicle: 'per voertuig',
            //     route: 'per route'
            // }

            // title += ` ${groupPart[selectedGroup]}`

            return (
                <div style={{padding: '12px 0', height: '100%'}}>
                    <Card
                        style={{
                            width: '100%',
                            height: '100%',
                            flex: 1,
                            padding: 12,
                            paddingTop: 0,
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        shadow
                    >
                        <div style={{marginBottom: 12}} />
                        <H3 style={{textAlign: 'center', marginBottom: 12}}>Totaal over periode</H3>
                        <div style={{marginBottom: 12}} />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: formSpacing.columnGap,
                                marginBottom: 24,
                                position: 'sticky',
                                top: 0,
                                paddingTop: 12,
                                backgroundColor: Colors.white
                            }}
                        >
                            <IconButton
                                icon={`mdi mdi-sort-${
                                    sortDescend ? 'descending' : 'ascending'
                                }`}
                                onClick={() => setSortDescend(!sortDescend)}
                            />
                            <Select
                                value={sortOn}
                                onChange={(e) => setSortOn(e.target.value as typeof sortOn)}
                                options={[
                                    {
                                        value: 'amount',
                                        title: 'Gemiddelde afwijking'
                                    },
                                    {
                                        value: 'amount_completed',
                                        title: 'Aantal uitgevoerd'
                                    },
                                    {
                                        value: 'alphabetic',
                                        title: 'Alfabetisch'
                                    }
                                ]}
                                label='Sorteer op'
                            />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder='Zoeken'
                                append='mdi mdi-magnify'
                            />
                        </div>
                        <div style={{overflow: 'auto', flex: 1}}>
                            <div style={{minHeight: '100%', maxHeight: '100%'}} ref={chartContainerRef}>
                                {!loading && (
                                    <HorizontalBarChart
                                        width={chartContainerWidth}
                                        height={chartContainerHeight}
                                        barHeight={24}
                                        data={differenceInRouteDurationPerGroupData}
                                        divergingData={differenceInRouteDurationPerGroupDivergingData}
                                        dataPrefix='-'
                                        dataSuffix='%'
                                        divergingDataPrefix='+'
                                        divergingDataSuffix='%'
                                        dataBarColor='#3077B6'
                                        divergingDataBarColor='#95C0DE'
                                        maxXAxis={60}
                                        showNameInShortBar={false}
                                        activeXTick={selectedSpecific}
                                        onClickXTick={(tick) =>
                                            setSelectedSpecific(
                                                selectedSpecific === tick ? undefined : tick
                                            )
                                        }
                                        highlightedXTick={highlightedXTick}
                                        sortYData={sortOn === 'amount_completed' ?
                                            (data) => data.sort((a, b) => {
                                                const aAmount = leftViewData.find((d) => d.name === a.name)?.amount || 0
                                                const bAmount = leftViewData.find((d) => d.name === b.name)?.amount || 0
                                                return bAmount - aAmount
                                            }) :
                                            undefined}
                                    />
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            )
        },
        mainView: ({loading, routesModel, analyticsStartDate, analyticsEndDate, selectedSpecific, setSelectedSpecific, selectedGroup}) => {
            const daysInRange = useMemo(() => {
                const startDate = moment(analyticsStartDate)
                const endDate = moment(analyticsEndDate)
                return endDate.diff(startDate, 'days')
            }, [analyticsStartDate, analyticsEndDate])

            const [chartInterval, setChartInterval] = useState<
        'day' | 'week' | 'month' | 'route'
            >(daysInRange < 32 ? 'day' : daysInRange >= 32 && daysInRange < 100 ? 'week' : 'month')
            const chartContainerRef = useRef<HTMLDivElement>(null)
            const [chartContainerWidth, setChartContainerWidth] = useState(1200)
            const [chartContainerHeight, setChartContainerHeight] = useState(700)

            useResizeObserver({
                ref: chartContainerRef,
                onResize: (width, height) => {
                    setChartContainerWidth(width)
                    setChartContainerHeight(height)
                }
            })

            useEffect(() => {
                if (!selectedSpecific && chartInterval === 'route') {
                    setChartInterval('day')
                }
            }, [selectedSpecific])

            useEffect(() => {
                if (daysInRange < 32) {
                    setChartInterval('day')
                } else if (daysInRange >= 32 && daysInRange < 100) {
                    setChartInterval('week')
                } else {
                    setChartInterval('month')
                }
            }, [daysInRange])

            const routesPerInterval = useMemo(() => {
                if (chartInterval === 'route') {
                    const routes = []

                    for (const [date, routesObjects] of Object.entries(routesModel)) {
                        const formatted = moment(date).format('DD-MM')

                        for (const [routeId, routeObj] of Object.entries(routesObjects)) {
                            const routeName = Object.keys(routeObj)[0]

                            routes.push({
                                date: `${routeName} ${formatted}`,
                                routes: [{[routeId]: routeObj}],
                                realDate: date
                            })
                        }
                    }

                    return routes
                }

                const grouped: Record<string, any[]> = {}

                for (const [modelDate, dateObj] of Object.entries(routesModel)) {
                    const key = moment(modelDate).startOf(chartInterval).format('YYYY-MM-DD')

                    if (!grouped[key]) grouped[key] = []
                    grouped[key].push(dateObj)
                }

                const dateGroups = getDateGroups(
                    moment(analyticsStartDate),
                    moment(analyticsEndDate),
                    chartInterval
                )

                return dateGroups.map((date) => {
                    const formattedDate = date.format('YYYY-MM-DD')

                    return {
                        date: formattedDate,
                        routes: grouped[formattedDate] ?? [],
                        realDate: undefined
                    }
                })
            }, [
                routesModel,
                analyticsStartDate,
                analyticsEndDate,
                chartInterval
            ])

            const chartData = useMemo(() => {
                const normal = []
                const diverging = []

                for (const {date, routes, realDate} of routesPerInterval) {
                    let totalPlannedDuration = 0
                    let totalDuration = 0
                    let isEmpty = false

                    for (const routeObj of routes) {
                        let obj = routeObj

                        if (selectedSpecific) {
                            const filtered = {}

                            for (const [routeId, object] of Object.entries(obj)) {
                                const groupLevel = pluckLevel(object, groupRoutesDepth[selectedGroup] - 2)[0]

                                const groupId = Object.keys(groupLevel)[0]

                                if (groupId === selectedSpecific) {
                                    filtered[routeId] = object
                                }
                            }

                            obj = filtered
                        }

                        if (chartInterval === 'route' && Object.keys(obj).length === 0) {
                            isEmpty = true
                            continue
                        }

                        const planned = pluckLevel(obj, groupRoutesDepth['plannedDuration'] - 1)

                        for (const o of Object.values(planned)) {
                            const key = Object.keys(o)[0]
                            if (key !== 'unknown') {
                                totalPlannedDuration += +key
                            }
                        }

                        const duration = pluckLevel(obj, groupRoutesDepth['duration'] - 1)

                        for (const o of Object.values(duration)) {
                            const key = Object.keys(o)[0]
                            if (key !== 'unknown') {
                                totalDuration += +key
                            }
                        }
                    }

                    if (totalDuration === 0 || totalPlannedDuration === 0) continue

                    const difference = ((totalDuration - totalPlannedDuration) / totalPlannedDuration) * 100

                    const entry = {
                        date,
                        value: Math.abs(Math.round(difference)),
                        isDiverging: difference > 0,
                        isEmpty,
                        realDate
                    }

                    if (chartInterval === 'route' && isEmpty) continue

                    if (entry.isDiverging) {
                        diverging.push(entry)
                    } else {
                        normal.push(entry)
                    }
                }

                return {
                    normal,
                    diverging
                }
            }, [
                chartInterval,
                selectedSpecific,
                routesPerInterval,
                selectedGroup
            ])

            const chart = useMemo(() => {
                return (
                    <VerticalBarChart
                        width={chartContainerWidth}
                        height={chartContainerHeight}
                        data={chartData.normal}
                        divergingData={chartData.diverging}
                        dataPrefix='-'
                        dataSuffix='%'
                        divergingDataPrefix='+'
                        divergingDataSuffix='%'
                        interval={chartInterval}
                        dataBarColor='#3077B6'
                        divergingDataBarColor='#95C0DE'
                        maxYAxis={50}
                    />
                )
            }, [
                chartContainerWidth,
                chartContainerHeight,
                chartData
            ])

            let title = `Afwijking routeduur`

            // const groupPart = {
            //     messenger: 'per koerier',
            //     vehicleType: 'per voertuigsoort',
            //     vehicle: 'per voertuig',
            //     route: 'per route'
            // }

            if (selectedSpecific) {
                title += ` voor ${selectedSpecific}`
            } else {
                // title += ` ${groupPart[selectedGroup]}`
            }

            return (
                <div
                    style={{
                        padding: '12px 0',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Card
                        style={{
                            flex: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 12
                        }}
                        shadow
                    >
                        <Row style={{justifyContent: 'space-between', alignItems: 'center'}}>
                            {selectedSpecific && (
                                <Button variant='outline' onClick={() => setSelectedSpecific(undefined)} icon='mdi mdi-arrow-left-thick' />
                            )}
                            <H3 style={{flex: 1, textAlign: 'center', marginBottom: 12}}>{title}</H3>
                            <div style={{display: 'flex', alignItems: 'center', columnGap: formSpacing.columnGap, marginBottom: 12}}>
                                <Select
                                    style={{width: 'fit-content', minWidth: 180}}
                                    label='Interval'
                                    value={chartInterval}
                                    onChange={(e) => setChartInterval(e.target.value as typeof chartInterval)}
                                    options={[
                                        {
                                            value: 'day',
                                            title: 'Per dag',
                                            disabled: daysInRange > 100
                                        },
                                        {
                                            value: 'week',
                                            title: 'Per week'
                                        },
                                        {
                                            value: 'month',
                                            title: 'Per maand'
                                        },
                                        {
                                            value: 'route',
                                            title: 'Per route',
                                            disabled: !selectedSpecific
                                        }
                                    ]}
                                />
                            </div>
                        </Row>
                        <div
                            ref={chartContainerRef}
                            style={{width: '100%', flex: 1, overflow: 'hidden'}}
                        >
                            {!loading && chart}
                        </div>
                    </Card>
                </div>
            )
        }
    }
]

/**
 * @param {AnalyticsProps} props
 * @returns {React.ReactNode} The rendered Analytics component
 */
export default function Analytics({reseller, history, branding}: AnalyticsProps) {
    const {
        analyticsStartDate,
        analyticsEndDate,
        analyticsQueries,
        analyticsModel,
        analyticsModelReversed,
        analyticsModelOriginal,
        parcelsModel,
        parcelsModelReversed,
        parcelsModelOriginal,
        stopsModel,
        stopsModelOriginal,
        routesModel,
        routesModelOriginal,
        selectedAnalytics,
        loading,
        selectedGroup,
        subGroup,
        showFilterCard,
        selectedSpecific
    } = useAnalyticsStore((state) => state)
    const [progress, setProgress] = useState<{
        progress: number
        estimatedTimeLeft: number
        amountOfDocs: number
        docsLeft: number
    }>()

    useEffect(() => {
        const shouldFetchBecauseNoData =
        _.isEmpty(analyticsModel) ||
        _.isEmpty(parcelsModel) ||
        _.isEmpty(stopsModel) ||
        _.isEmpty(routesModel)

        const lastStart = useAnalyticsStore.getState().lastFetchedStartDate
        const lastEnd = useAnalyticsStore.getState().lastFetchedEndDate

        const datesChanged = analyticsStartDate !== lastStart || analyticsEndDate !== lastEnd

        if (!shouldFetchBecauseNoData && !datesChanged) return

        const fetchData = async () => {
            useAnalyticsStore.setState({loading: true})

            const socketEvent = guid.generate().substring(0, 8)
            socket.on(socketEvent, setProgress)

            await getAnalyticsModels(socketEvent)

            setLastFetchedStartDate(analyticsStartDate)
            setLastFetchedEndDate(analyticsEndDate)

            if (analyticsQueries[selectedAnalytics]?.length > 0) {
                filterQueries(selectedAnalytics)
            }

            useAnalyticsStore.setState({loading: false})
            socket.off(socketEvent)

            setProgress({
                progress: 0,
                estimatedTimeLeft: 0,
                amountOfDocs: 0,
                docsLeft: 0
            })
        }

        fetchData()
    }, [analyticsStartDate, analyticsEndDate])

    useEffect(() => {
        document.title = `Rapportage • ${
            reseller.settings.accountName || reseller.name || 'Veloyd'
        }`
    }, [reseller.name, reseller.settings.accountName])

    const onDateChange = (startDate, endDate) => {
        setAnalyticsDates(startDate, endDate)
    }

    const groupingIsReversed = useMemo(() => {
        if (selectedAnalytics === 'amountOfAddresses') {
            const groupDepth = groupDepths[selectedGroup['amountOfAddresses']]
            const subGroupDepth = subGroup ? groupDepths[subGroup['amountOfAddresses']] : undefined
            const isReversed = subGroup ? subGroupDepth < groupDepth : false
            return isReversed
        }

        if (selectedAnalytics === 'amountOfParcels') {
            const groupDepth = parcelsGroupDepth[selectedGroup['amountOfParcels']]
            const subGroupDepth = subGroup ? parcelsGroupDepth[subGroup['amountOfParcels']] : undefined
            const isReversed = subGroup ? subGroupDepth < groupDepth : false
            return isReversed
        }

        // other analytics views don't have reversed grouping and models
        return false
    }, [selectedAnalytics, selectedGroup, subGroup])

    const {
        // amountOfOrders,
        amountOfAddresses,
        // amountOfDeliveryAddresses,
        // amountOfPickupAddresses,
        // amountOfColli,
        amountOfParcels,
        // amountOfStopsPlanned,
        // amountOfStopsCompleted,
        // amountOfStopsDelayed,
        // deliveryAttemptsPercentageDelivered,
        deliveryAttemptsPercentageOffered,
        differenceInRouteDuration,
        legend,
        leftViewData,
        mainViewData
    } = useMemo(() => {
        // console.log('start')
        // console.log('done')
        // return {
        //     amountOfOrders: 0,
        //     amountOfAddresses: 0,
        //     amountOfDeliveryAddresses: 0,
        //     amountOfPickupAddresses: 0,
        //     amountOfColli: 0,
        //     amountOfParcels: 0,
        //     amountOfStopsPlanned: 0,
        //     amountOfStopsCompleted: 0,
        //     amountOfStopsDelayed: 0,
        //     deliveryAttemptsPercentageDelivered: 0,
        //     deliveryAttemptsPercentageOffered: 0,
        //     differenceInRouteDuration: '0',
        //     legend: [],
        //     leftViewData: [],
        //     mainViewData: []
        // }
        let sumOrders = 0
        let sumAddresses = 0
        let sumDeliveryAddresses = 0
        let sumPickupAddresses = 0
        let sumColli = 0
        let sumParcels = 0
        let sumStopsPlanned = 0
        // let sumDeliveryAttemptsOffered = 0
        // let sumDeliveryAttemptsDelivered = 0
        let differenceInRouteDuration = '0'
        let legend: Legend = []

        let legendGroupLevel = []
        let legendGroupDepth: number | undefined = undefined
        let legendSubGroupDepth: number | undefined = undefined

        let leftViewData: ViewData[] = []
        let mainViewData: ViewData[] = []

        // For when selectedAnalytics is 'amountOfAddresses'
        sumAddresses = sumCountsRecursive(analyticsModel)

        const allDeliveryAndPickupObjects = (
            pluckLevel(analyticsModel, 2) as AddressesModel[string][string][]
        ).reduce<{
            delivery: {
                [key: number]: AddressesModel[string][string][string]
            }
            pickup: {
                [key: number]: AddressesModel[string][string][string]
            }
        }>(
            (filtered, obj, index) => {
                Object.entries(obj).forEach(([key, typeObj]) => {
                    filtered[key] = filtered[key] || {}
                    filtered[key][index] = typeObj
                })
                return filtered
            },
            {
                delivery: {},
                pickup: {}
            }
        )

        sumDeliveryAddresses = sumCountsRecursive(
            allDeliveryAndPickupObjects['delivery']
        )
        sumPickupAddresses = sumCountsRecursive(
            allDeliveryAndPickupObjects['pickup']
        )

        let groupDepth = groupDepths[selectedGroup[selectedAnalytics]]
        let subGroupDepth = subGroup ? groupDepths[subGroup[selectedAnalytics]] : undefined

        if (groupingIsReversed) {
            groupDepth = groupReversedDepths[selectedGroup[selectedAnalytics]]
            subGroupDepth = groupReversedDepths[subGroup[selectedAnalytics]]
        }

        let groupedLevel = pluckLevel(groupingIsReversed ? analyticsModelReversed : analyticsModel, groupDepth)
        if (shouldSplitAnalyticsGroup(selectedGroup[selectedAnalytics])) {
            let newGroupedLevel = []
            groupedLevel.forEach((groupLevel) => {
                Object.entries(groupLevel).forEach(([optionsString, obj]) => {
                    const options = splitAnalyticsGroupValues(optionsString)
                    options.forEach((option) => {
                        newGroupedLevel.push({
                            [option]: obj
                        })
                    })
                })
            })

            groupedLevel = newGroupedLevel
        }
        const modelsGroup = _.uniq(
            groupedLevel.map((obj) => Object.keys(obj)).flatMap((k) => k)
        )

        const amountOfAddressesPerGroup: ViewData[] = modelsGroup.map((group) => {
            const obj = {}

            groupedLevel.forEach((groupObj, index) => {
                Object.keys(groupObj).forEach((g) => {
                    if (g === group) {
                        obj[index] = groupObj[g]
                    }
                })
            })

            return {
                specificId: group,
                name: group,
                modelDepth: groupDepth,
                amount: sumCountsRecursive(obj),
                obj
            }
        })

        if (selectedAnalytics === 'amountOfAddresses') {
            legendGroupLevel = groupedLevel
            legendGroupDepth = groupDepth
            if (subGroup) {
                legendSubGroupDepth = subGroupDepth
            }

            leftViewData = amountOfAddressesPerGroup
            mainViewData = amountOfAddressesPerGroup

            if (selectedGroup[selectedAnalytics] === 'total') {
                leftViewData = [
                    {
                        specificId: 'total',
                        name: 'Totaal',
                        modelDepth: groupDepths['total'],
                        amount: sumAddresses,
                        obj: analyticsModel
                    }
                ]
                mainViewData = leftViewData
            }
        }

        // End of 'amountOfAddresses'

        // Start of 'amountOfParcels'
        sumParcels = sumCountsRecursive(parcelsModel)

        let parcelsGroupingDepth = parcelsGroupDepth[selectedGroup[selectedAnalytics]]
        let parcelsSubgroupDepth = parcelsGroupDepth[subGroup[selectedAnalytics]]

        if (groupingIsReversed) {
            parcelsGroupingDepth = parcelsGroupReversedDepth[selectedGroup[selectedAnalytics]]
            parcelsSubgroupDepth = parcelsGroupReversedDepth[subGroup[selectedAnalytics]]
        }

        let groupedParcelLevel = pluckLevel(
            groupingIsReversed ? parcelsModelReversed : parcelsModel,
            parcelsGroupingDepth
        )
        if (shouldSplitAnalyticsGroup(selectedGroup[selectedAnalytics])) {
            let newGroupedLevel = []
            groupedParcelLevel.forEach((groupLevel) => {
                Object.entries(groupLevel).forEach(([optionsString, obj]) => {
                    const options = splitAnalyticsGroupValues(optionsString)
                    options.forEach((option) => {
                        newGroupedLevel.push({
                            [option]: obj
                        })
                    })
                })
            })

            groupedParcelLevel = newGroupedLevel
        }

        const parcelsModelsGroup = _.uniq(
            groupedParcelLevel.map((obj) => Object.keys(obj)).flatMap((k) => k)
        )

        const amountOfParcelsPerGroup: ViewData[] = parcelsModelsGroup.map(
            (group) => {
                const obj = {}
                groupedParcelLevel.forEach((groupObj, index) => {
                    Object.keys(groupObj).forEach((g) => {
                        if (g === group) {
                            obj[index] = groupObj[g]
                        }
                    })
                })

                if (selectedGroup['amountOfParcels'] === 'amountOfDaysTillDelivery' && group === 'Niet bezorgd') {
                    return undefined
                }

                return {
                    specificId: group,
                    name: group,
                    modelDepth: parcelsGroupingDepth,
                    amount: sumCountsRecursive(obj),
                    obj
                }
            }
        ).filter((el) => el)

        if (selectedAnalytics === 'amountOfParcels') {
            legendGroupLevel = groupedParcelLevel
            legendGroupDepth = parcelsGroupingDepth
            if (subGroup) {
                legendSubGroupDepth = parcelsSubgroupDepth
            }

            leftViewData = amountOfParcelsPerGroup
            mainViewData = amountOfParcelsPerGroup

            if (selectedGroup[selectedAnalytics] === 'total') {
                leftViewData = [
                    {
                        specificId: 'total',
                        name: 'Totaal',
                        modelDepth: parcelsGroupDepth['total'],
                        amount: sumParcels,
                        obj: parcelsModel
                    }
                ]
                mainViewData = leftViewData
            }
        }

        // End of 'amountOfParcels'

        // Start of 'plannedStops'
        interface CompletedGrouped {
            completed: {
                [key: number]: {
                    count: number
                }
            }
            delayed: {
                [key: number]: {
                    count: number
                }
            }
        }

        sumStopsPlanned = sumCountsRecursive(stopsModel)

        const isCompletedLevel = pluckLevel(
            stopsModel,
            groupStopsDepth['isCompleted']
        ) as StopsModel[string][string][string][string][string][]
        const completedGrouped = isCompletedLevel.reduce<CompletedGrouped>(
            (grouped, obj, index) => {
                Object.entries(obj).forEach(([key, countObj]) => {
                    if (key === 'false') {
                        if (grouped['delayed'][index]?.count) {
                            grouped['delayed'][index].count += sumCountsRecursive(countObj)
                        } else {
                            grouped['delayed'][index] = {
                                count: sumCountsRecursive(countObj)
                            }
                        }
                    }

                    if (key === 'true') {
                        if (grouped['completed'][index]?.count) {
                            grouped['completed'][index].count += sumCountsRecursive(countObj)
                        } else {
                            grouped['completed'][index] = {
                                count: sumCountsRecursive(countObj)
                            }
                        }
                    }
                })
                return grouped
            },
            {
                completed: {},
                delayed: {}
            }
        )

        const isDeliveredLevel = pluckLevel(
            stopsModel,
            groupStopsDepth['isDelivered']
        ) as StopsModel[string][string][string][string][string][string][]
        const isDeliveredSum = isDeliveredLevel.reduce((sum, obj) => {
            Object.entries(obj).forEach(([key, countObj]) => {
                if (key === 'true') {
                    sum += sumCountsRecursive(countObj)
                }
            })
            return sum
        }, 0)

        let groupedStopsLevel = pluckLevel(
            stopsModel,
            groupStopsDepth[selectedGroup[selectedAnalytics]]
        )
        if (selectedGroup[selectedAnalytics] === 'deliveryZone') {
            let newGroupedLevel = []
            groupedStopsLevel.forEach((groupLevel) => {
                Object.entries(groupLevel).forEach(([optionsString, obj]) => {
                    const options = splitAnalyticsGroupValues(optionsString)
                    options.forEach((option) => {
                        newGroupedLevel.push({
                            [option]: obj
                        })
                    })
                })
            })

            groupedStopsLevel = newGroupedLevel
        }
        const modelsStopsGroup = _.uniq(
            groupedStopsLevel.map((obj) => Object.keys(obj)).flatMap((k) => k)
        )

        const amountOfStopsPerGroup: ViewData[] = modelsStopsGroup.map((group) => {
            const obj = {}

            groupedStopsLevel.forEach((groupObj, index) => {
                Object.keys(groupObj).forEach((g) => {
                    if (g === group) {
                        obj[index] = groupObj[g]
                    }
                })
            })

            return {
                specificId: group,
                name: group,
                modelDepth: groupStopsDepth[selectedGroup[selectedAnalytics]],
                amount: sumCountsRecursive(obj)
            }
        })

        if (selectedAnalytics === 'plannedStops') {
            leftViewData = amountOfStopsPerGroup
            mainViewData = amountOfStopsPerGroup

            if (selectedGroup[selectedAnalytics] === 'total') {
                leftViewData = [
                    {
                        specificId: 'total',
                        name: 'Totaal',
                        modelDepth: groupStopsDepth['total'],
                        amount: sumStopsPlanned,
                        obj: stopsModel
                    }
                ]
                mainViewData = leftViewData
            }
        }

        // End of 'plannedStops'

        // Start of 'amountOfDeliveryAttempts'
        const deliveryAttemptsPercentageOffered =
      (sumCountsRecursive(completedGrouped.completed) / sumStopsPlanned) * 100
        const deliveryAttemptsPercentageDelivered =
      (isDeliveredSum / sumStopsPlanned) * 100

        const amountOfPercentagesPerGroup: ViewData[] = modelsStopsGroup.map(
            (group) => {
                const obj = {}
                let isCompletedSum = 0
                let isDeliveredSum = 0

                groupedStopsLevel.forEach((groupObj, index) => {
                    Object.keys(groupObj).forEach((g) => {
                        if (g === group) {
                            obj[index] = groupObj[g]

                            const isCompletedLevel =
                groupStopsDepth['isCompleted'] - groupStopsDepth[selectedGroup[selectedAnalytics]]
                            const pluckedCompleted = pluckLevel(
                                groupObj[g],
                                isCompletedLevel - 1
                            )
                            pluckedCompleted.forEach((obj) => {
                                Object.entries(obj).forEach(([isCompleted, obj]) => {
                                    if (isCompleted === 'true') {
                                        isCompletedSum += sumCountsRecursive(obj)
                                    }
                                })
                            })

                            const isDeliveredLevel =
                groupStopsDepth['isDelivered'] - groupStopsDepth[selectedGroup[selectedAnalytics]]
                            const pluckedDelivered = pluckLevel(
                                groupObj[g],
                                isDeliveredLevel - 1
                            )
                            pluckedDelivered.forEach((obj) => {
                                Object.entries(obj).forEach(([isDelivered, obj]) => {
                                    if (isDelivered === 'true') {
                                        isDeliveredSum += sumCountsRecursive(obj)
                                    }
                                })
                            })
                        }
                    })
                })

                const totalSum = sumCountsRecursive(obj)

                return {
                    specificId: group,
                    name: group,
                    modelDepth: groupStopsDepth[selectedGroup[selectedAnalytics]],
                    amount: totalSum,
                    deliveryAttemptsPercentages: {
                        delivered: parseFloat(
                            ((isDeliveredSum / totalSum) * 100).toFixed(2)
                        ),
                        offered: parseFloat(((isCompletedSum / totalSum) * 100).toFixed(2))
                    },
                    deliveryAttempts: {
                        delivered: isDeliveredSum,
                        offered: isCompletedSum
                    }
                }
            }
        )

        if (selectedAnalytics === 'amountOfDeliveryAttempts') {
            legend = [
                {
                    label: 'Bezorgd',
                    color: '#3077B6'
                },
                {
                    label: 'Aangeboden',
                    color: '#95C0DE',
                    buttonInfo: `Aangeboden en bezorgd percentages worden geteld per collo voor stops die je zelf hebt uitgevoerd.

Elke bezorgpoging wordt geteld op de datum van uitvoering.

Een collo valt in de categorie Bezorgd, Aangeboden of Niet aangeboden, of heeft een status die niet wordt meegeteld. Hieronder staat per categorie welke statussen er onder vallen.

Bezorgd:
    • Alle afgerond statussen

Aangeboden:
    • Alle afgerond statussen
    • Niet thuis
    • Adres onjuist
    • Bedrijfsadres gesloten
    • Verificatie niet gelukt
    • Geweigerd

Niet aangeboden:
    • Gereed
    • Onderweg
    • Manco
    • Adres niet gevonden
    • Ontbreekt
    • Tijdnood

Statussen die niet meegeteld worden:
    • Verwacht
    • Niet aangeleverd
    • Niet opgehaald
    • In bewaring
    • Geannuleerd
    • Overmacht
    • Anders
                        `
                }
            ]

            leftViewData = amountOfPercentagesPerGroup
            mainViewData = amountOfPercentagesPerGroup

            if (selectedGroup[selectedAnalytics] === 'total') {
                leftViewData = [
                    {
                        specificId: 'total',
                        name: 'Totaal',
                        modelDepth: groupStopsDepth['total'],
                        amount: sumCountsRecursive(groupedStopsLevel),
                        deliveryAttemptsPercentages: {
                            delivered: deliveryAttemptsPercentageDelivered,
                            offered: deliveryAttemptsPercentageOffered
                        },
                        deliveryAttempts: {
                            delivered: isDeliveredSum,
                            offered: sumCountsRecursive(completedGrouped.completed)
                        }
                    }
                ]
                mainViewData = leftViewData
            }
        }
        // End of 'amountOfDeliveryAttempts'

        // Start of 'differenceRouteDuration'
        const totalPlannedDuration = pluckLevel(
            routesModel,
            groupRoutesDepth['plannedDuration']
        ).reduce((sum, obj) => {
            const plannedDuration = Object.keys(obj)[0]
            if (plannedDuration === 'unknown') {
                return sum
            }

            return sum + parseInt(plannedDuration)
        }, 0)

        const totalDuration = pluckLevel(
            routesModel,
            groupRoutesDepth['duration']
        ).reduce((sum, obj) => {
            const duration = Object.keys(obj)[0]
            if (duration === 'unknown') {
                return sum
            }

            return sum + parseInt(duration)
        }, 0)

        const differenceInPercentage =
      ((totalDuration - totalPlannedDuration) / totalPlannedDuration) * 100

        if (isNaN(differenceInPercentage)) {
            differenceInRouteDuration = ''
        } else {
            const formattedValue = Number(differenceInPercentage.toFixed(2))
            if (formattedValue >= 0) {
                differenceInRouteDuration = `+${formattedValue.toLocaleString('nl-NL')}`
            } else {
                differenceInRouteDuration = formattedValue.toLocaleString('nl-NL')
            }
        }

        const groupedRoutesLevel = pluckLevel(
            routesModel,
            groupRoutesDepth[selectedGroup[selectedAnalytics]]
        )
        const modelsRoutesGroup = _.uniq(
            groupedRoutesLevel.map((obj) => Object.keys(obj)).flatMap((k) => k)
        )

        const differenceRouteDurationPerGroup: ViewData[] = modelsRoutesGroup.flatMap((group) => {
            const obj = {}
            let plannedDuration = 0
            let duration = 0

            groupedRoutesLevel.forEach((groupObj, index) => {
                Object.keys(groupObj).forEach((g) => {
                    if (g === group) {
                        obj[index] = groupObj[g]

                        const durationDeep: number = pluckLevel(groupObj, groupRoutesDepth['duration'] - groupRoutesDepth[selectedGroup[selectedAnalytics]]).reduce((sum, obj) => {
                            const duration = Object.keys(obj)[0]
                            if (duration === 'unknown') {
                                return sum
                            }

                            return sum + parseInt(duration)
                        }, 0)

                        if (durationDeep === 0) {
                            return
                        }

                        const plannedDurationDeep: number = pluckLevel(groupObj, groupRoutesDepth['plannedDuration'] - groupRoutesDepth[selectedGroup[selectedAnalytics]]).reduce((sum, obj) => {
                            const duration = Object.keys(obj)[0]
                            if (duration === 'unknown') {
                                return sum
                            }

                            return sum + parseInt(duration)
                        }, 0)

                        plannedDuration += plannedDurationDeep

                        duration += durationDeep

                        // const differenceInPercentage = (((duration - plannedDuration) / plannedDuration) * 100) || 0

                        // return {
                        //     specificId: group,
                        //     name: group,
                        //     modelDepth: groupStopsDepth[selectedGroup],
                        //     amount: sumCountsRecursive(groupObj),
                        //     differenceInRouteDuration: differenceInPercentage
                        // }
                    }
                })
            })

            const differenceInPercentage = (((duration - plannedDuration) / plannedDuration) * 100) || 0

            if (duration === 0 || plannedDuration === 0) {
                return undefined
            }

            return {
                specificId: group,
                name: group,
                modelDepth: groupRoutesDepth[selectedGroup[selectedAnalytics]],
                amount: sumCountsRecursive(obj),
                differenceInRouteDuration: differenceInPercentage,
                obj
            }
        }).filter((d) => d)

        if (selectedAnalytics === 'differenceRouteDuration') {
            leftViewData = differenceRouteDurationPerGroup
            mainViewData = differenceRouteDurationPerGroup
        }

        if (legendGroupLevel.length > 0 && !(selectedGroup[selectedAnalytics] === 'total' && subGroup[selectedAnalytics] === '')) {
            const uniqueGroupLabels: {
                [key: string]: {
                    label: string
                    value: number
                }
            } = legendGroupLevel.reduce((previous, groupObj) => {
                Object.keys(groupObj).forEach((key) => {
                    if (selectedGroup['amountOfParcels'] === 'amountOfDaysTillDelivery' && key === 'Niet bezorgd') {
                        return
                    }

                    if (typeof legendSubGroupDepth === 'number') {
                        let subGroups = pluckLevel(groupObj, legendSubGroupDepth - (legendGroupDepth - 1) - 1)

                        if (shouldSplitAnalyticsGroup(subGroup[selectedAnalytics])) {
                            let newSubGroups = []
                            subGroups.forEach((groupObj) => {
                                Object.entries(groupObj).forEach(([optionsString, obj]) => {
                                    const options = splitAnalyticsGroupValues(optionsString)
                                    options.forEach((option) => {
                                        newSubGroups.push({
                                            [option]: obj
                                        })
                                    })
                                })
                            })

                            subGroups = newSubGroups
                        }

                        const parcelsSubGroup = subGroup['amountOfParcels']
                        subGroups.forEach((subGroup) => {
                            Object.keys(subGroup).forEach((subGroupKey) => {
                                if (parcelsSubGroup === 'amountOfDaysTillDelivery' && subGroupKey === 'Niet bezorgd') {
                                    return
                                }

                                if (previous[subGroupKey]) {
                                    previous[subGroupKey].value += sumCountsRecursive(subGroup[subGroupKey])
                                } else {
                                    previous[subGroupKey] = {
                                        label: subGroupKey,
                                        value: sumCountsRecursive(subGroup[subGroupKey])
                                    }
                                }
                            })
                        })
                    } else {
                        if (previous[key]) {
                            previous[key].value += sumCountsRecursive(groupObj[key])
                        } else {
                            previous[key] = {
                                label: key,
                                value: sumCountsRecursive(groupObj[key])
                            }
                        }
                    }
                })
                return previous
            }, {})

            legend = Object.values(uniqueGroupLabels)
                .sort((a, b) => b.value - a.value)
                .slice(0, 7)
                .map((g, index) => {
                    const color = legendColorMap[index] || '#000000'
                    return {
                        label: g.label,
                        color
                    }
                })

            if (Object.keys(uniqueGroupLabels).length > 7) {
                legend.push({
                    label: 'Overig',
                    color: legendColorMap[7] || '#000000'
                })
            }
        }

        // console.log('done')
        return {
            amountOfOrders: sumOrders,
            amountOfAddresses: sumAddresses,
            amountOfDeliveryAddresses: sumDeliveryAddresses,
            amountOfPickupAddresses: sumPickupAddresses,
            amountOfColli: sumColli,
            amountOfParcels: sumParcels,
            amountOfStopsPlanned: sumStopsPlanned,
            amountOfStopsCompleted: sumCountsRecursive(completedGrouped.completed),
            amountOfStopsDelayed: sumCountsRecursive(completedGrouped.delayed),
            deliveryAttemptsPercentageDelivered,
            deliveryAttemptsPercentageOffered,
            differenceInRouteDuration,
            legend,
            leftViewData,
            mainViewData
        }
    }, [
        analyticsModel,
        analyticsModelReversed,
        parcelsModel,
        parcelsModelReversed,
        stopsModel,
        routesModel,
        selectedGroup,
        // selectedSpecific,
        selectedAnalytics,
        subGroup,
        analyticsQueries
    ])

    const selectedView = useMemo(() => {
        return analyticsViews.find((view) => view.key === selectedAnalytics)!
    }, [selectedAnalytics, analyticsViews])

    const insightCardsText = {
        amountOfAddresses: amountOfAddresses.toLocaleString('nl-NL'),
        amountOfParcels: amountOfParcels.toLocaleString('nl-NL'),
        amountOfDeliveryAttempts: isNaN(deliveryAttemptsPercentageOffered) ?
            '-' :
            `${deliveryAttemptsPercentageOffered
                .toFixed(1)
                .replace('.', ',')}%`,
        differenceRouteDuration: differenceInRouteDuration === '-' ? differenceInRouteDuration : `${differenceInRouteDuration}%`
    }

    const selectedViewFilterOptions = useMemo(() => {
        const filterOptions = {}
        if (loading) {
            return filterOptions
        }
        const filterOnOptions = selectedView.subFilterBar.groupOptions.filter((option) => option.value !== 'total')

        filterOnOptions.forEach((option) => {
            const depth: number = {
                amountOfAddresses: groupDepths[option.value],
                amountOfParcels: parcelsGroupDepth[option.value],
                amountOfDeliveryAttempts: groupStopsDepth[option.value],
                differenceRouteDuration: groupRoutesDepth[option.value]
            }[selectedView.key]
            const model = {
                amountOfAddresses: analyticsModelOriginal,
                amountOfParcels: parcelsModelOriginal,
                amountOfDeliveryAttempts: stopsModelOriginal,
                differenceRouteDuration: routesModelOriginal
            }[selectedView.key]
            const plucked = pluckLevel(model, depth)

            // Use a Set to ensure uniqueness
            const uniqueValues = new Set<string>()

            plucked.forEach((obj) => {
                const values = Object.keys(obj)

                values.forEach((value) => {
                    if (
                        shouldSplitAnalyticsGroup(option.value) &&
                        (selectedView.key === 'amountOfAddresses' || selectedView.key === 'amountOfParcels' || selectedView.key === 'amountOfDeliveryAttempts')
                    ) {
                        splitAnalyticsGroupValues(value).forEach((splitValue) => {
                            if (!uniqueValues.has(splitValue)) {
                                uniqueValues.add(splitValue)
                            }
                        })
                    } else {
                        if (!uniqueValues.has(value)) {
                            uniqueValues.add(value)
                        }
                    }
                })
            })

            filterOptions[option.value] = Array.from(uniqueValues).map((val) => ({
                title: val,
                value: val
            }))
        })

        return filterOptions
    }, [selectedView, loading])

    return (
        <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Panel
                style={{
                    display: 'flex',
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    background: Colors.backgroundNeutral,
                    padding: '20px 10px 10px 10px'
                }}
            >
                <SearchBar
                    showSearch={false}
                    queries={[]}
                    onQueryChange={() => {}}
                    onDateChange={onDateChange}
                    startDate={analyticsStartDate}
                    endDate={analyticsEndDate}
                    columns={[
                        {
                            key: 'customer',
                            title: 'Klant'
                        },
                        {
                            key: 'postalCodeZone',
                            title: 'Postcodegebied'
                        },
                        {
                            key: 'deliveryZone',
                            title: 'Bezorggebied'
                        },
                        {
                            key: 'option',
                            title: 'Verzendoptie'
                        },
                        {
                            key: 'amountOfDaysBetweenHubScanAndCompleted',
                            title: 'Aantal dagen tot bezorging (stops)'
                        },
                        {
                            key: 'carrier',
                            title: 'Vervoerder'
                        },
                        {
                            key: 'status',
                            title: 'Status'
                        },
                        {
                            key: 'koppeling',
                            title: 'Koppeling'
                        },
                        {
                            key: 'amountOfDaysTillDelivery',
                            title: 'Aantal dagen tot bezorging (zendingen)'
                        },
                        {
                            key: 'country',
                            title: 'Land'
                        },
                        {
                            key: 'messenger',
                            title: 'Koerier'
                        },
                        {
                            key: 'route',
                            title: 'Routenaam'
                        },
                        {
                            key: 'vehicleType',
                            title: 'Voertuigsoort'
                        },
                        {
                            key: 'vehicle',
                            title: 'Voertuig'
                        }
                    ]}
                    onTimeChange={() => { }}
                    minDate='2025-01-01'
                    maxRange={365}
                />
            </Panel>
            {loading && (
                <div
                    style={{
                        position: 'fixed',
                        zIndex: 500,
                        inset: 0,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(0,0,0,0.45)'
                    }}
                >
                    <div style={{width: 600, background: Colors.white, padding: 24, borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12}}>
                        <Spinner />

                        <ProgressBar
                            style={{marginBottom: 16}}
                            now={progress?.progress}
                            max={1}
                        />
                    </div>
                </div>
            )}
            <div
                style={{
                    padding: '0 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    overflowY: 'hidden'
                }}
            >
                <div style={{padding: '12px 0'}}>
                    <Row style={{gap: 12}}>
                        <InsightsBar
                            cards={analyticsViews
                                .filter((v) => !v.disabled)
                                .map((view) => ({
                                    title: view.title,
                                    icon: view.icon,
                                    key: view.key,
                                    text: insightCardsText[view.key]
                                }))}
                            selectedAnalytics={selectedAnalytics}
                            setSelectedAnalytics={(key) => {
                                setSelectedSpecific('')
                                setSelectedAnalytics(key)
                            }}
                            branding={branding}
                        />
                    </Row>
                </div>

                <AnalyticsView
                    title={selectedView.title}
                    icon={selectedView.icon}
                    key={selectedView.key}
                    subFilterBar={selectedView.subFilterBar}
                    LeftView={selectedView.leftView}
                    leftViewProps={{
                        loading,
                        legend,
                        subGroup: subGroup[selectedAnalytics],
                        selectedGroup: selectedGroup[selectedAnalytics],
                        selectedSpecific,
                        setSelectedSpecific,
                        groupingIsReversed,
                        leftViewData,
                        subFilterBar: selectedView.subFilterBar
                    }}
                    MainView={selectedView.mainView}
                    mainViewProps={{
                        loading,
                        legend,
                        subGroup: subGroup[selectedAnalytics],
                        selectedGroup: selectedGroup[selectedAnalytics],
                        selectedSpecific,
                        setSelectedSpecific,
                        groupingIsReversed,
                        mainViewData,
                        analyticsStartDate,
                        analyticsEndDate,
                        analyticsModel,
                        analyticsModelReversed,
                        parcelsModel,
                        parcelsModelReversed,
                        stopsModel,
                        routesModel,
                        history
                    }}
                    loading={loading}
                    selectedGroup={selectedGroup[selectedAnalytics]}
                    setSelectedSpecific={setSelectedSpecific}
                    setSubGroup={(newSubGroup) => {
                        setSubGroup(selectedAnalytics, newSubGroup)
                    }}
                    setSelectedGroup={(newGroup) => {
                        setSelectedGroup(selectedAnalytics, newGroup)
                    }}
                    selectedSubGroup={subGroup[selectedAnalytics]}
                    showSubGroup={selectedAnalytics !== 'amountOfDeliveryAttempts' && selectedAnalytics !== 'differenceRouteDuration'}
                    legend={legend}
                    filterOnOptions={selectedView.subFilterBar.groupOptions.filter((option) => option.value !== 'total')}
                    filterOptions={selectedViewFilterOptions}
                    activeFilterQueries={analyticsQueries[selectedAnalytics]}
                    onChangeQueries={(queries) => {
                        setAnalyticsQueries(queries, selectedAnalytics)
                    }}
                    showFilterCard={showFilterCard}
                    setShowFilterCard={setShowFilterCard}
                />
            </div>
        </div>
    )
}
